module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('h1', {
    staticClass: "chapter-no",
    attrs: {
      "id": "ch-1"
    }
  }, [_vm._v("CHAPTER 1")]), _vm._v(" "), _c('h1', {
    staticClass: "chapter-title1"
  }, [_vm._v("Issues in Interpreting and Preaching Old Testament Law")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("In his "), _c('em', [_vm._v("Reclaiming the Old Testament for the Christian Pulpit")]), _vm._v(", Donald Gowan writes, “How—and whether—to preach the laws of the Old Testament presents perhaps the most difficult theological problem we shall encounter in this book, since the place of law in the Christian life has remained one of the perennial issues of our faith since the time of Jesus himself.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_1",
      "id": "fn-1"
    }
  }, [_vm._v("1")])]), _vm._v(" David Dorsey echoes this sentiment: “One of the most difficult portions of the Bible to utilize in Christian teaching and preaching is the corpus of Mosaic laws.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_2",
      "id": "fn-2"
    }
  }, [_vm._v("2")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("But it would be a mistake to focus only on the difficulties of preaching Leviticus. As pointed out in the preface, Leviticus “contains more direct speech by God himself than any other book of the Bible and it is placed at the heart of the Torah or Pentateuch …, which forms the foundation for all of Scripture.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_3",
      "id": "fn-3"
    }
  }, [_vm._v("3")])]), _vm._v(" Arie Leder underscores the importance of Leviticus for Israel by observing, “Without the instructions of Leviticus, Israel’s life before God has no chance of survival. She has been separated from the nations to receive the special instruction to live among the nations as God’s special people (Lev 15:31; 20:26, ‘to separate,’ or ‘to set apart’)…. Without Leviticus, Israel would be just another among many nations in Canaan.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_4",
      "id": "fn-4"
    }
  }, [_vm._v("4")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In contrast to people who think that Leviticus is all about rituals, Jacob Milgrom points out that “values are what Leviticus is all about. They pervade every chapter and almost every verse…. Leviticus "), _c('em', [_vm._v("does")]), _vm._v(" discuss rituals. However, underlying the rituals, the careful readers will find an intricate web of values that purports to model how we should relate to God and to one another.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_5",
      "id": "fn-5"
    }
  }, [_vm._v("5")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Skipping over Leviticus, as many preachers do, deprives congregations of essential biblical values on how we should relate to God and to each other. Disregarding Leviticus also deprives congregations of essential biblical foundations for understanding the work and demands of Christ. So it will be worth our while to work our way through the difficulties. Our congregations will be richly rewarded not only in better understanding the message of Leviticus and the mission of Christ but also in discovering what this book means for the church today. John Hartley summarizes its relevance: “Leviticus provides an understanding of holiness-sin-atonement, the essential elements of the divine-human relationship. Furthermore, just as Israel was called to be a holy nation in order to function as a royal priesthood to the nations, the church is called to be a holy people in order that it may serve all the peoples of the world as a royal priesthood.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_6",
      "id": "fn-6"
    }
  }, [_vm._v("6")])]), _vm._v(" We therefore begin working our way through the difficulties with solid hope for good and relevant outcomes.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-1"
    }
  }, [_vm._v("Difficulties in Preaching Old Testament Law")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-1"
    }
  }, [_vm._v("The Issue of Continuity and Discontinuity")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("One of the problems in preaching Old Testament law is the lack of clarity in determining the continuity and/or discontinuity of God’s law, first given to Israel, for the New Testament church. For example, we are quick to invoke discontinuity when it comes to the dietary laws of Leviticus 11, which forbid the eating of pork, crab, lobster, rabbits, and water hens. We tend to do the same with laws about not reaping “to the very edges of your field” (Lev 19:9–10) and laws concerning religious feasts (Lev 23), Sabbatical Years, and the Year of Jubilee (Lev 25). We are a little more ambivalent about laws concerning the tithe (Lev 27:30–33). Most Christians don’t tithe, but many evangelicals do. How does one determine when a law is intended only for God’s Old Testament people or also for the church?")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Our ambivalence regarding Old Testament law has led to various denominational practices: most Christian churches have worship services on Sunday, but Seventh-Day Adventists and Seventh-Day Baptists worship on Saturday (“the seventh day”). Most Christians don’t mind eating blood in rare steaks and blood sausage (contrary to Lev 17:10–14; cf. Acts 15:29), but Jehovah’s Witnesses refuse blood transfusions.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("People have taken various positions on the issue of the continuity or discontinuity of Old Testament laws. The two extreme positions are, on the one hand, classic dispensationalism, which argues that the Jewish dispensation was characterized by law and the Christian dispensation by grace."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_7",
      "id": "fn-7"
    }
  }, [_vm._v("7")])]), _vm._v(" That position does not seem to leave much room for continuity between Old Testament law and its relevance for the church today. On the other hand, theonomy or Christian reconstructionism argues for strict continuity: “the Old Testament laws and penalties are still in effect today.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_8",
      "id": "fn-8"
    }
  }, [_vm._v("8")])]), _vm._v(" Most denominations take positions between these two extremes."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_9",
      "id": "fn-9"
    }
  }, [_vm._v("9")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The major problem in resolving this issue is the discontinuity created by the historical-cultural gap that separates Old Testament Israel from the New Testament church. In fact, there are more gaps than just history and culture. Giving many examples, David Dorsey names five gaps between the body of Mosaic laws and the New Testament church:")]), _vm._v(" "), _c('p', {
    staticClass: "ext"
  }, [_vm._v("First, the corpus is designed to regulate the lives of a people living in the distinctive "), _c('em', [_vm._v("geographical and climatic conditions")]), _vm._v(" found in the southern Levant [the East] and the majority of its regulations would be inapplicable, unintelligible, or even nonsensical outside that geographically limited locale…. Second, the corpus was designed to regulate the lives of a people whose "), _c('em', [_vm._v("cultural milieu")]), _vm._v(" was that of the ancient Near East…. Third, the corpus was designed to regulate the lives of people whose "), _c('em', [_vm._v("religious milieu")]), _vm._v(" was that of the ancient Near East (particularly Canaan) and would make little sense outside that world…. Fourth, the corpus lays the detailed groundwork for and regulates the various affairs of "), _c('em', [_vm._v("an actual politically-and geographically-defined nation")]), _vm._v("…. Fifth, the corpus is designed to establish and maintain "), _c('em', [_vm._v("a cultic regime")]), _vm._v(" which was restricted to ancient Israel and has been discontinued in the Church (cf. Heb. chs 7–10).")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Dorsey sums up: “Virtually all the regulations of the corpus—certainly ninety-five percent—are culturally restricted, geographically limited, and cultically and politically specific, and as a result are inapplicable to, and in fact unfulfillable by, Christians living throughout the world today.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_10",
      "id": "fn-10"
    }
  }, [_vm._v("10")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-2"
    }
  }, [_vm._v("The Issue of Law and Gospel")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("In the Reformed tradition the issue of continuity/discontinuity took on the form of law and gospel. In his “Preface to the Old Testament” (1523) Luther writes, “The Old Testament is a book of laws, which teaches what men are to do and not to do … just as the New Testament is gospel or book of grace, and teaches where one is to get the power to fulfil the law…. Just as the chief teaching of the New Testament is really the proclamation of grace and peace through the forgiveness of sins in Christ, so the chief teaching of the Old Testament is really the teaching of laws, the showing up of sin, and the demanding of God.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_11",
      "id": "fn-11"
    }
  }, [_vm._v("11")])]), _vm._v(" Thus Luther saw two main functions of the law: first, "), _c('em', [_vm._v("usus civilis")]), _vm._v("—to restrain evil in this sinful world; and second, "), _c('em', [_vm._v("usus paedagogicus")]), _vm._v("—a schoolmaster convicting us of sin and driving us to Christ. Many Lutherans have followed Luther in advocating this limited use of Old Testament law for Christians. For example, William Thompson writes, “The point of Christ’s death and resurrection is that on the cross he satisfied the demands of the law. Paul put the argument insightfully and elegantly in Romans 8:2. ‘For the law of the Spirit of life in Christ Jesus has set me free from the law of sin and death.’ … The intent of the law in Christian theology is to awaken us to our distance from the will and purpose of God; it is not to provide us with ethical instruction. The Ten Commandments do not primarily teach us how to live; they document the depth of our guilt before God.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_12",
      "id": "fn-12"
    }
  }, [_vm._v("12")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("John Calvin, by contrast, advocated a third use of the law: "), _c('em', [_vm._v("usus tertius")]), _vm._v(" or, more descriptively, "), _c('em', [_vm._v("usus normativus")]), _vm._v(". Calvin writes, “The third and principal use, which pertains more closely to the proper purpose of the law, finds its place among believers in whose hearts the Spirit of God already lives and reigns…. They still profit by the law in two ways. Here is the best instrument for them to learn more thoroughly each day the nature of the Lord’s will to which they aspire, and confirm them in the understanding of it.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_13",
      "id": "fn-13"
    }
  }, [_vm._v("13")])]), _vm._v(" Calvin’s positive assessment of Old Testament law is related to his view of God’s covenant: God first redeems Israel from slavery in Egypt and then makes a covenant with them at Sinai. Also in the Old Testament, therefore, God’s redemption (grace) is first and is followed by God’s law. In other words, God’s grace displayed in redemption requires a subsequent response of obedience to God’s law.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-3"
    }
  }, [_vm._v("Proposed Solution: A Threefold Distinction between Old Testament Laws")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Traditionally theologians made a threefold distinction between Old Testament laws: moral law (e.g., the Ten Commandments), which is valid for all time; civil law, which pertained specifically to the nation of Israel but not to the church; and ceremonial law, the sacrificial/ritual laws that were fulfilled by Christ’s sacrifice and therefore no longer hold for the church. If this distinction were valid, there would be little point in preaching Old Testament civil and ceremonial laws. Since large parts of Leviticus are “ceremonial” (e.g., Lev 1:1–7:38; 11:1–12:8; 16:1–17:16) and “civil,” much of Leviticus would be removed from the preaching agenda.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("There are, however, major problems with this threefold distinction. First, as many authors have noted, it is not a biblical distinction."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_14",
      "id": "fn-14"
    }
  }, [_vm._v("14")])]), _vm._v(" The New Testament nowhere makes this threefold distinction. And the Old Testament freely mixes so-called moral, civil, and ceremonial laws. For example, the “moral” law “You shall not take vengeance or bear a grudge against any of your people, but you shall love your neighbor as yourself: I am the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v("” (Lev 19:18) is followed by “You shall keep my statutes. You shall not let your animals breed with a different kind; you shall not sow your field with two kinds of seed; nor shall you put on a garment made of two different materials” (Lev 19:19). The reason the Old Testament freely mixes these categories of law is that it considered all of life to be under God’s dominion; hence every part of life was regulated by God’s law."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_15",
      "id": "fn-15"
    }
  }, [_vm._v("15")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Moreover, it is difficult to clearly distinguish between moral, civil, and ceremonial law. For example, is the Sabbath commandment moral, ceremonial, or civil? Are the Old Testament laws regulating feasts only ceremonial or also civil? In addition, civil legislation frequently made concrete the moral demands of the Decalogue. For example, the moral law “You shall not murder” (Exod 20:13) is made concrete in a civil law: “When you build a new house, you shall make a parapet for your roof; otherwise you might have "), _c('em', [_vm._v("bloodguilt")]), _vm._v(" on your house, if anyone should fall from it” (Deut 22:8). Or, to take an example from Leviticus, the moral law “You shall love your neighbor as yourself” (Lev 19:18) is made concrete in the civil legislation regarding the Year of Jubilee: forgive those indebted to you, set Israelite slaves free, and “you shall return, every one of you, to your property and every one of you to your family” (Lev 25:10).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Although Calvin used this threefold distinction of “the ancient writers,” he notes that ceremonial and civil laws embody moral principles that still apply to the New Testament church."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_16",
      "id": "fn-16"
    }
  }, [_vm._v("16")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-4"
    }
  }, [_vm._v("A Better Solution: Torah")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Instead of prejudging the applicability of Old Testament laws for the New Testament church with this traditional threefold distinction, a better solution is to understand all these different laws as torah—that is, God’s instruction,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_17",
      "id": "fn-17"
    }
  }, [_vm._v("17")])]), _vm._v(" God’s teaching, for Israel’s well-being in all areas of life. For the Old Testament Israelite, torah did not have the negative connotations that “law” sometimes has for us today; it had the positive connotations that the word “gospel” has for us. Moses said to Israel, “And what other great nation has statutes and ordinances as just as this entire law that I am setting before you today?” (Deut 4:8). Torah is God’s gift to his people Israel.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("When God saved his people from slavery in Egypt, he made a covenant with them at Mount Sinai and gave them his torah. This means that the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v("’"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("S")]), _vm._v(" torah are his "), _c('em', [_vm._v("covenant stipulations")]), _vm._v(". This perspective on torah has major implications. As Gordon Wenham puts it, “These laws are more than an abstract system of morality. They are the "), _c('em', [_vm._v("personal demands")]), _vm._v(" of the sovereign, personal God on his subject people.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_18",
      "id": "fn-18"
    }
  }, [_vm._v("18")])]), _vm._v(" Moreover, Wenham continues, “The covenant setting of the law emphasizes that "), _c('em', [_vm._v("salvation is not based on works.")]), _vm._v(" The covenant was made with those who had already been saved from Egypt…. The priority and absoluteness of God’s grace are constantly reiterated…. Law both presupposes grace and is a means of grace. Law presupposes grace because law is only revealed to those God has called to himself. Law is a means of grace because through obedience to it the redeemed enter into a closer relationship to their divine king and enjoy more of the blessings inherent within the state of salvation.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_19",
      "id": "fn-19"
    }
  }, [_vm._v("19")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In addition, we should note, with Klein, Blomberg, and Hubbard, that")]), _vm._v(" "), _c('p', {
    staticClass: "ext"
  }, [_vm._v("instead of a code in a modern sense, Old Testament laws present a select sample of illustrative cases or topics whose legal principles were to guide Israelite individuals, the larger community, and lawmakers in making decisions and in living out Israel’s worldview. Their purpose was to teach the Israelite fundamental values—what it means to live all of life in the presence of God—not to provide them with a handy legal reference tool. In short, their aim was instructional rather than judicial…. In light of this, readers must interpret Law relationally—as the guidelines that govern Israel’s ongoing life with her gracious God. In return for his protection and blessing, God expects his people to obey what the Law commands—in short, to maintain their relationship with God on a healthy footing. The Ten Commandments (Exod 20; Deut 5) express the broad, overarching ethical principles whose details the subsequent legal codes flesh out."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_20",
      "id": "fn-20"
    }
  }, [_vm._v("20")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Exodus 20 begins, “Then God spoke all these words: I am the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" your God, who brought you out of the land of Egypt, out of the house of slavery” (vv 1–2). Then follow the Ten Commandments prescribing the road Israel must follow to maintain that God-given freedom. And not only the Ten Commandments mark the road to freedom, but all God’s laws do: “You shall keep my statutes and my ordinances; by doing so "), _c('em', [_vm._v("one shall live")]), _vm._v(": I am the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v("” (Lev 18:5; cf. Deut 30:15–20). The Creator’s torah enables God’s people to live with the grain of the universe.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Psalm 19 sings about God’s torah:")]), _vm._v(" "), _c('p', {
    staticClass: "poem1"
  }, [_vm._v("The law of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" is perfect,")]), _vm._v(" "), _c('p', {
    staticClass: "poem2"
  }, [_vm._v("reviving the soul;")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_vm._v("the decrees of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" are sure,")]), _vm._v(" "), _c('p', {
    staticClass: "poem2"
  }, [_vm._v("making wise the simple;")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_vm._v("the precepts of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" are right,")]), _vm._v(" "), _c('p', {
    staticClass: "poem2"
  }, [_vm._v("rejoicing the heart;")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_vm._v("the commandment of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" is clear,")]), _vm._v(" "), _c('p', {
    staticClass: "poem2"
  }, [_vm._v("enlightening the eyes;")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_vm._v("the fear of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" is pure,")]), _vm._v(" "), _c('p', {
    staticClass: "poem2"
  }, [_vm._v("enduring forever;")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_vm._v("the ordinances of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" are true")]), _vm._v(" "), _c('p', {
    staticClass: "poem2"
  }, [_vm._v("and righteous altogether.")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_vm._v("More to be desired are they than gold,")]), _vm._v(" "), _c('p', {
    staticClass: "poem2"
  }, [_vm._v("even much fine gold;")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_vm._v("sweeter also than honey,")]), _vm._v(" "), _c('p', {
    staticClass: "poem2"
  }, [_vm._v("and drippings of the honeycomb.")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_vm._v("Moreover by them is your servant warned;")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_vm._v("in keeping them there is great reward. (vv 7–11)"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_21",
      "id": "fn-21"
    }
  }, [_vm._v("21")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para1"
  }, [_vm._v("In the Sermon on the Mount, Jesus says, “Do not think that I have come to abolish the law or the prophets; I have come not to abolish but to fulfill. For truly I tell you, until heaven and earth pass away, not one letter, not one stroke of a letter, will pass from the law until all is accomplished” (Matt 5:17–18). Jesus did not abrogate the law. He "), _c('em', [_vm._v("fulfilled")]), _vm._v(" it, gave the full meaning to it."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_22",
      "id": "fn-22"
    }
  }, [_vm._v("22")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Later Paul asked, “Do we then overthrow the law by this faith?” And he answered himself, “By no means! On the contrary, we uphold the law” (Rom 3:31). In Romans 7:12 Paul concludes, “So the law is holy, and the commandment is holy and just and good.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_23",
      "id": "fn-23"
    }
  }, [_vm._v("23")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-5"
    }
  }, [_vm._v("Continuity in the Midst of Discontinuity")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("It is true that because of the progression in redemptive history, many Old Testament laws no longer hold for the church. The once-for-all sacrifice of Jesus Christ abrogated many Old Testament sacrificial laws. Also, since the Old Testament nation of Israel became the New Testament international church, there is discontinuity between some of these Old Testament laws and their application to the New Testament church. For example, instead of Old Testament dietary restrictions, Jesus “declared all foods clean” (Mark 7:19; cf. Rom 14:14). Later, God told Peter in a vision, “What God has made clean, you must not call profane” (Acts 10:15). The author of Hebrews argues that “since the law has only a shadow of the good things to come,” “when Christ came into the world” he “abolished” “sacrifices and offerings and burnt offerings and sin offerings” (10:1–10). Paul argues forcefully that even the pre-Mosaic sign of the covenant, circumcision, which went back to the time of Abraham, was no longer required: “Listen! I, Paul, am telling you that if you let yourselves be circumcised, Christ will be of no benefit to you” (Gal 5:2).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Discontinuity exists because Jesus established the new covenant Jeremiah had predicted: “The days are surely coming, says the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(", when I will make a new covenant with the house of Israel and the house of Judah. It will not be like the covenant that I made with their ancestors when I took them by the hand to bring them out of the land of Egypt—a covenant that they broke, though I was their husband, says the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(". But this is the covenant that I will make with the house of Israel after those days, says the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(": I will put my "), _c('em', [_vm._v("law")]), _vm._v(" within them, and I will write it on their hearts; and I will be their God, and they shall be my people” (Jer 31:31–33)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_24",
      "id": "fn-24"
    }
  }, [_vm._v("24")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Jesus established this new covenant when he instituted the Lord’s Supper the night before he died. Jesus said, “This cup that is poured out for you is the "), _c('em', [_vm._v("new covenant")]), _vm._v(" in my blood” (Luke 22:20). Paul later writes to the Corinthians that God “has made us competent to be ministers of a "), _c('em', [_vm._v("new covenant")]), _vm._v(", not of letter but of spirit; for the letter kills, but the Spirit gives life” (2 Cor 3:6). And the author of Hebrews asserts unequivocally, “In speaking of ‘a new covenant,’ he has made the first one obsolete. And what is obsolete and growing old will soon disappear” (8:13). So there are discontinuities between the old covenant and the new, between Old Testament law and New Testament law, between Old Testament Israel and the New Testament church.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("But there is continuity in the midst of discontinuity. “Under the old covenant Israel was saved in the exodus from Egypt. She was expected to respond by accepting the kingship of Yahweh and obeying his covenant law. Under the new covenant the new Israel is saved by the coming of Christ. She is expected to respond by acknowledging that Christ is Lord and following his teaching.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_25",
      "id": "fn-25"
    }
  }, [_vm._v("25")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("When the first church council in Jerusalem had to consider the question of whether Gentile believers should still be circumcised in the new age Jesus inaugurated, it responded, “It has seemed good to the Holy Spirit and to us to impose on you no further burden than these essentials: that you abstain from what has been sacrificed to idols and from blood and from what is strangled and from fornication. If you keep yourselves from these, you will do well. Farewell” (Acts 15:28–29). The council found continuity in the midst of discontinuity, for the laws it considered still binding on the Gentiles were thought to go back to God’s regulations for Noah and all peoples (Gen 9:4–5)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_26",
      "id": "fn-26"
    }
  }, [_vm._v("26")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("We can approach the continuity in the midst of discontinuity from two closely related angles. First, as Jay Sklar makes clear, the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v("’"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("S")]), _vm._v(" laws reflect God’s will or, as Sklar puts it, "), _c('em', [_vm._v("God’s values")]), _vm._v(". And “since his values flow from his character, and his character is perfect and constant (Mal 3:6; Heb 13:8; Jas 1:17), it should be expected that the values behind these laws will have some application today. Stated differently, these laws give us a window into the Lord’s heart, meaning there is much to learn from them for those seeking to reflect his will…. While not every law is in force today as it was originally expressed, the values embodied in every law remain valid and should be lived out by the people of God today.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_27",
      "id": "fn-27"
    }
  }, [_vm._v("27")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Second, as Richard Averbeck notes, the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v("’"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("S")]), _vm._v(" laws express "), _c('em', [_vm._v("God’s expectations")]), _vm._v(": “The law continues to demonstrate God’s expectations, though Christians live under the new covenant and have a different relationship to the Mosaic law (cf. Acts 15)…. Although Jesus fulfilled the sacrificial requirements for salvation, that does not make the ceremonial law inapplicable. On the contrary, the ceremonial worship law is especially suited for defining the expectations of the Christian faith…. The issue then is not whether the Old Testament law is applicable today, but how it is applicable.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_28",
      "id": "fn-28"
    }
  }, [_vm._v("28")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("One can find the application of these ancient laws for today by looking for the principles behind the individual laws. As Gordon Wenham puts it, “The principles underlying the OT are valid and authoritative for the Christian, but the particular applications found in the OT may not be. The moral principles are the same today, but insofar as our situation often differs from the OT setting, the application of the principles in our society may well be different too.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_29",
      "id": "fn-29"
    }
  }, [_vm._v("29")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("For example, we noted above that the “moral” law “You shall not murder” (Exod 20:13) is made concrete in a “civil” law: “When you build a new house, you shall make a parapet for your roof; otherwise you might have bloodguilt on your house, if anyone should fall from it” (Deut 22:8). Most new houses in America do not have a flat roof where people can enjoy the cool of the day as they did in Israel. So the making of a parapet may not apply. But the principle underlying it, “You shall not murder,” still applies. How, specifically?")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("My wife and I recently moved to a fifth-floor apartment. Since a fall from a fifth-floor balcony could be deadly, the builder (no doubt following city code) added a solid metal guardrail: “You shall not murder.” Tremper Longman observes, “We can immediately sense the relevance of this law today by asking the question, Where would a fence protect human life? One obvious answer is, around a swimming pool. Someone who builds a pool but fails to construct a fence around it should be held liable if a neighbor child wanders into the area and drowns.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_30",
      "id": "fn-30"
    }
  }, [_vm._v("30")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In preaching Old Testament law, then, the preacher’s task is to push through the discontinuity to the continuity by searching for the underlying principle"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_31",
      "id": "fn-31"
    }
  }, [_vm._v("31")])]), _vm._v(" of which this particular law was a concrete application in Israel and to determine how this principle would be made concrete today.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-2"
    }
  }, [_vm._v("Interpreting Leviticus")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The English name Leviticus comes from the Septuagint "), _c('em', [_vm._v("Leuiticon")]), _vm._v(" via the Vulgate "), _c('em', [_vm._v("Leviticus")]), _vm._v(". While many commentators translate this word as “relating to the Levite,” Jacob Milgrom points out that “in Hellenistic times, the term ‘Levites’ meant priests,” just like “the rabbinic title "), _c('em', [_vm._v("torat kohanim")]), _vm._v(" ‘the manual of the Priests.’”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_32",
      "id": "fn-32"
    }
  }, [_vm._v("32")])]), _vm._v(" Accordingly, the book has detailed instructions for Israel’s priests. But Baruch Levine observes that “"), _c('em', [_vm._v("torat kohanim")]), _vm._v(" can also be translated as ‘instructions "), _c('em', [_vm._v("of")]), _vm._v(" (or "), _c('em', [_vm._v("by")]), _vm._v(") the priests,’ that is, the rulings and teachings of the priests that are addressed to the Israelite people.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_33",
      "id": "fn-33"
    }
  }, [_vm._v("33")])]), _vm._v(" As a matter of fact, Leviticus contains both instructions for the priests and for the people of Israel."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_34",
      "id": "fn-34"
    }
  }, [_vm._v("34")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Leviticus follows very naturally from the book of Exodus. Exodus traces God’s preparations to deliver Israel from enslavement in Egypt and describes the exodus itself and the journey to Sinai where God makes a covenant with Israel and instructs them to build a tabernacle. The book ends with the completion of the tabernacle and the glory of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" filling the tabernacle. Leviticus follows appropriately with instructions for priests, Levites, and laity regarding the worship services conducted at the tabernacle. Leviticus “addresses how Israel was to live with God in their midst. How should they approach Him? How should they deal with personal and national sin before a holy God who dwelled among them? How should they worship and fellowship with this holy, awesome God in their midst? Leviticus provides the answers to these questions, giving practical guidelines for living with God under the terms of the Mosaic Covenant.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_35",
      "id": "fn-35"
    }
  }, [_vm._v("35")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("But Sklar rightly observes that “Leviticus does more than answer questions raised by its immediate literary and historical context. It also casts a vision rooted in the Bible’s larger story and, in particular, in creation. Indeed, God’s purpose for his people in Leviticus is in many ways a return to his purpose for humanity in creation. This may be seen in terms of separation, blessing and calling.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_36",
      "id": "fn-36"
    }
  }, [_vm._v("36")])]), _vm._v(" Leviticus “casts a vision that takes the Israelites back to the Lord’s intent for humanity from the beginning of the world: to walk in rich fellowship with their covenant King, enjoying his care and blessing, and extending throughout all the earth his kingdom of justice, mercy, kindness, righteousness, holiness and love.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_37",
      "id": "fn-37"
    }
  }, [_vm._v("37")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("It is ironic that many preachers skip Leviticus as presumably unimportant when the editors of the Pentateuch marked it as very important by placing it at the center of the Pentateuch. As my colleague Arie Leder explains,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_38",
      "id": "fn-38"
    }
  }, [_vm._v("38")])])]), _vm._v(" "), _c('p', {
    staticClass: "ext"
  }, [_vm._v("In the concentric organization of the Pentateuch, parallels between Exodus and Numbers suggest that they constitute a frame for Leviticus. Parallels between Genesis and Deuteronomy not only frame Exodus - Leviticus - Numbers thematically, they also provide the beginning and conclusion of the linear sequence of the entire pentateuchal narrative. Thus, Genesis through Deuteronomy exhibits an ABCB′A′ organizational format in which Deuteronomy returns to and complements the themes of Genesis, and Numbers returns to and complements the themes of Exodus. This leaves Leviticus occupying the narrative center of the Pentateuch, as illustrated in the adapted chart below.")]), _vm._v(" "), _c('p', {
    staticClass: "para-h1"
  }, [_c('strong', [_vm._v("A—Genesis")]), _c('span', {
    staticClass: "sp"
  }, [_vm._v("Separation from the nations "), _c('br'), _vm._v("Blessing "), _c('br'), _vm._v("Seeing the land "), _c('br'), _vm._v("Descendants and the land")])]), _vm._v(" "), _c('p', {
    staticClass: "para-h2"
  }, [_c('strong', [_vm._v("B—Exodus")]), _c('span', {
    staticClass: "sp"
  }, [_vm._v("Israel’s desert journeys "), _c('br'), _vm._v("Apostasy and plagues "), _c('br'), _vm._v("Pharaoh and magicians "), _c('br'), _vm._v("First-born/Levites")])]), _vm._v(" "), _c('p', {
    staticClass: "para-h3"
  }, [_c('strong', [_vm._v("C—Leviticus")]), _c('span', {
    staticClass: "sp"
  }, [_vm._v("Sacrifices "), _c('br'), _vm._v("Cleanliness "), _c('br'), _vm._v("Holiness")])]), _vm._v(" "), _c('p', {
    staticClass: "para-h4"
  }, [_c('strong', [_vm._v("B′—Numbers")]), _c('span', {
    staticClass: "sp"
  }, [_vm._v("Israel’s desert journeys "), _c('br'), _vm._v("Apostasy and plagues "), _c('br'), _vm._v("Balak and Balaam "), _c('br'), _vm._v("First-born/Levites")])]), _vm._v(" "), _c('p', {
    staticClass: "para-h5"
  }, [_c('strong', [_vm._v("A′—Deuteronomy")]), _c('span', {
    staticClass: "sp"
  }, [_vm._v("Separation from the nations "), _c('br'), _vm._v("Blessing "), _c('br'), _vm._v("Seeing the land "), _c('br'), _vm._v("Descendants and the land")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para1"
  }, [_vm._v("But why did the editors of the Pentateuch consider Leviticus of central importance? For the answer we have to look to the beginning of the Pentateuch. Michael Morales observes, “While Genesis 1 portrays creation as a tabernacle, Genesis 2–3 portrays the garden of Eden as something of an archetypal holy of holies—the place of most intimate communion and fellowship with YHWH God.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_39",
      "id": "fn-39"
    }
  }, [_vm._v("39")])]), _vm._v(" Tragically, because of the human fall into sin, that intimate communion and fellowship with God was lost: God exiled humanity to a place east of Eden. But God still intended to dwell in the midst of his people. Making a covenant with Israel at Sinai, he instructed them to build a tabernacle. When the tabernacle was completed, Exodus 40:34 reports, “the cloud covered the tent of meeting, and the glory of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" filled the tabernacle.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_40",
      "id": "fn-40"
    }
  }, [_vm._v("40")])]), _vm._v(" God dwelled in the midst of his people, a foreshadowing of the restoration of Eden and his people’s intimate communion with God.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("But how can sinful humans approach the sovereign, holy God without being annihilated? Leviticus responds to that age-old longing of the human race to live in fellowship with God with the following answers: sacrifices, cleanliness, and holiness. As Morales puts it, “The primary theme and theology of Leviticus (and of the Pentateuch as a whole) is YHWH’s opening a way for humanity to dwell in the divine presence.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_41",
      "id": "fn-41"
    }
  }, [_vm._v("41")])]), _vm._v(" Toward the end of Leviticus God promises, “I will place my dwelling in your midst, and I shall not abhor you. And I will walk among you, and will be your God, and you shall be my people” (26:11–12).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("It has been said, “If a person can preach from Leviticus effectively, a person can preach from anywhere in the Bible!”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_42",
      "id": "fn-42"
    }
  }, [_vm._v("42")])]), _vm._v(" It is certainly true that preaching Leviticus provides preachers with many challenges. As we have seen, Leviticus contains mostly so-called ceremonial and civil laws that were directly applicable only to Israel. Preachers therefore have to inquire about the reason why God gave these laws to Israel (which is not always obvious), the principle underlying these laws, how one can move to Christ in the New Testament from these laws or their underlying principle, and how the underlying principle might be applied today.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("To accomplish this, we must interpret Leviticus in four dimensions: we begin with literary interpretation because we have the literary text in front of us; from there we move on to historical interpretation, which searches behind the text for the historical situation that gave rise to (the need for) the text; then we move on to theocentric interpretation; and finally we arrive at Christocentric interpretation. We shall briefly explain each dimension and what it contributes to understanding Leviticus.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-6"
    }
  }, [_vm._v("Literary Interpretation")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("In literary interpretation (broadly conceived as inclusive of grammatical concerns) we pay particular attention to the meaning of the words by investigating literary features such as the text’s structure, genre, grammar, syntax, figures of speech, scenes, character description, plotline, and rhetorical structures such as repetition, inclusio, and chiasm.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Leviticus begins with information vital to its interpretation: “The L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" summoned Moses and spoke to him from the tent of meeting, saying: Speak to the people of Israel and say to them …” (Lev 1:1–2). This opening provides several clues on how we ought to understand Leviticus. First, the main characters are the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(", Moses, and Israel. Second, “the scene is set at the tent of meeting,”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_43",
      "id": "fn-43"
    }
  }, [_vm._v("43")])]), _vm._v(" where the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" “meets” his people. The tent of meeting plays a key role in the book (the word is repeated forty-three times in Leviticus). Third, the book consists mostly of Moses’ speeches to Israel. Fourth, it was the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" who instructed Moses on what to say (cf. Lev 26:46 and 27:34).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Repetition provides a clue to the overall theme of Leviticus. “‘Holy’ ("), _c('em', [_vm._v("qā")]), _c('span', {
    staticClass: "f1"
  }, [_vm._v("ḏ")]), _c('em', [_vm._v("ôš")]), _vm._v(") and its cognate terms, e.g., ‘sanctify,’ ‘holiness,’ occur 152 times in Leviticus (about 20 percent of the total occurrences in the OT).”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_44",
      "id": "fn-44"
    }
  }, [_vm._v("44")])]), _vm._v(" Five times the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" commands Israel, “Be holy, for I am holy” (Lev 11:44–45 [2×]; 19:2; 20:7, 26). The overall theme of Leviticus, therefore, is that the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v("’"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("S")]), _vm._v(" people should be holy because the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" is holy.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Julie Smith points out that “Leviticus relies on analogical thinking, which means that each part of the law cannot be understood on its own but only by comparing it with other parts of the law of Moses…. In Leviticus there are usually no explanations given for why something is done: rather, the explanation is to be found in comparing one part of the text with another part of the text.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_45",
      "id": "fn-45"
    }
  }, [_vm._v("45")])]), _vm._v(" In the following chapters we shall highlight analogical interpretation in the sections “Text and Context.”")]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-1"
    }
  }, [_vm._v("Outline of Leviticus")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("For a quick overview of Leviticus we shall look at several proposals for its outline. Most commentators outline Leviticus as five blocks of related material:")]), _vm._v(" "), _c('ol', [_c('li', [_vm._v("Instructions for sacrifices (chs. 1–7)")]), _vm._v(" "), _c('li', [_vm._v("The ordination of priests (chs. 8–10)")]), _vm._v(" "), _c('li', [_vm._v("Laws of cleanliness (chs. 11–15)")]), _vm._v(" "), _c('li', [_vm._v("The Day of Atonement (ch. 16)")]), _vm._v(" "), _c('li', [_vm._v("The Holiness Code (chs. 17–27)")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In "), _c('em', [_vm._v("The NIV Study Bible")]), _vm._v(" R. Laird Harris and Ronald Youngblood go into more detail on the Holiness Code by dividing Leviticus into ten categories:")]), _vm._v(" "), _c('ol', [_c('li', [_vm._v("The five main offerings (chs. 1–7)")]), _vm._v(" "), _c('li', [_vm._v("The ordination, installation and work of Aaron and his sons (chs. 8–10)")]), _vm._v(" "), _c('li', [_vm._v("Laws of cleanness—food, childbirth, infections, etc. (chs. 11–15)")]), _vm._v(" "), _c('li', [_vm._v("The Day of Atonement and the centrality of worship at the tabernacle (chs. 16–17)")]), _vm._v(" "), _c('li', [_vm._v("Moral laws covering incest, honesty, thievery, idolatry, etc. (chs. 18–20)")]), _vm._v(" "), _c('li', [_vm._v("Regulations for the priests, the offerings and the annual feasts (chs. 21:1–24:9)")]), _vm._v(" "), _c('li', [_vm._v("Punishment for blasphemy, murder, etc. (ch. 24:10–23)")]), _vm._v(" "), _c('li', [_vm._v("The Sabbath Year, Jubilee, land tenure and reform of slavery (ch. 25)")]), _vm._v(" "), _c('li', [_vm._v("Blessings and curses for covenant obedience and disobedience (ch. 26)")]), _vm._v(" "), _c('li', [_vm._v("Regulations for offerings vowed to the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" (ch. 27)"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_46",
      "id": "fn-46"
    }
  }, [_vm._v("46")])])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-2"
    }
  }, [_vm._v("Rhetorical Structures")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Scholars have offered several proposals that divide Leviticus into rhetorical structures. Most agree that chapter 16, the Day of Atonement, is the structural center."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_47",
      "id": "fn-47"
    }
  }, [_vm._v("47")])]), _vm._v(" Around that center several have suggested a chiastic structure. We shall look at only one proposal that seems quite convincing:")]), _vm._v(" "), _c('p', {
    staticClass: "parac-10"
  }, [_vm._v("A"), _c('span', {
    staticClass: "sp"
  }, [_vm._v("Cultic legislation (chs. 1–7)")])]), _vm._v(" "), _c('p', {
    staticClass: "parac21"
  }, [_vm._v("B"), _c('span', {
    staticClass: "sp"
  }, [_vm._v("Priestly history (chs. 8–10)")])]), _vm._v(" "), _c('p', {
    staticClass: "parac-22"
  }, [_vm._v("C"), _c('span', {
    staticClass: "sp"
  }, [_vm._v("Personal laws of uncleanness (chs. 11–15)")])]), _vm._v(" "), _c('p', {
    staticClass: "parac-23"
  }, [_vm._v("D"), _c('span', {
    staticClass: "sp"
  }, [_vm._v("Day of Atonement (ch. 16)")])]), _vm._v(" "), _c('p', {
    staticClass: "parac-22"
  }, [_vm._v("C′"), _c('span', {
    staticClass: "sp1"
  }, [_vm._v("Personal moral laws (chs. 17–20)")])]), _vm._v(" "), _c('p', {
    staticClass: "parac21"
  }, [_vm._v("B′"), _c('span', {
    staticClass: "sp1"
  }, [_vm._v("Priestly legislation (chs. 21–22)")])]), _vm._v(" "), _c('p', {
    staticClass: "parac-20"
  }, [_vm._v("A′"), _c('span', {
    staticClass: "sp1"
  }, [_vm._v("Cultic legislation (chs. 23–25, except history in 24:10–23)")])]), _vm._v(" "), _c('p', {
    staticClass: "parac-30"
  }, [_vm._v("Conclusion (chs. 26–27)"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_48",
      "id": "fn-48"
    }
  }, [_vm._v("48")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-3"
    }
  }, [_vm._v("Narrative Structure")]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("The overall genre of Leviticus seems to be law. But Daniel Hays argues persuasively that Leviticus not only contains legal material but also has narrative features. He writes, “The Book of Leviticus is also painted on a narrative canvas against the backdrop of the encounter with God at Mount Sinai (Lev 26:46; 27:34). The Law in Leviticus is presented as part of a dialogue between God and Moses. Such use of dialogue is a standard feature of narrative. The book begins, ‘The L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" called to Moses and spoke to him from the Tent of Meeting.’ The phrase ‘The L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" said [spoke] to Moses’ occurs repeatedly throughout the book. In addition Leviticus includes numerous time sequence phrases, an indication of storyline time movement, another characteristic of narrative.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_49",
      "id": "fn-49"
    }
  }, [_vm._v("49")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In fact, Christophe Nihan argues that Leviticus is “a section of a wider narrative extending from the world’s creation to Moses’ death…. These speeches are reported speeches by the Leviticus narrator and, as such, they belong fully to the wider pentateuchal narrative. Within this narrative, Leviticus presents itself as the account of a specific revelation made to Moses from inside the sanctuary (Lev 1:1) at Mt Sinai (27:34), taking place in the time span between Israel’s arrival at Mt Sinai in Exodus and the community’s sojourn in the wilderness in Numbers…. The book’s coherence is ultimately given by its "), _c('em', [_vm._v("narrative")]), _vm._v(" structure.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_50",
      "id": "fn-50"
    }
  }, [_vm._v("50")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("But where in Leviticus is the narrative conflict, the problem to be resolved? The usual biblical plotlines begin with a conflict that leads to a climax which is gradually resolved until full resolution is obtained and an outcome."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_51",
      "id": "fn-51"
    }
  }, [_vm._v("51")])]), _vm._v(" Where is the conflict? Nihan has pointed out that the conflict to be resolved occurs in Exodus when the tent of meeting was completed: “Then the cloud covered the tent of meeting, and the glory of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" filled the tabernacle. "), _c('em', [_vm._v("Moses was not able to enter the tent of meeting")]), _vm._v(" because the cloud settled upon it, and the glory of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" filled the tabernacle” (40:34–35). Moses, who had mediated between God and the people on Mount Sinai, could not enter the tent of meeting! And if Moses could not come near to the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(", no human being could. Nihan writes, “Although he [the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v("] is present among his people as promised in Ex 25:8 and 29:45, Yahweh cannot be approached, even by Moses, and the gap between God and man remains insuperable. After Ex 40, Lev 1–10 recounts the gradual abolishment of this gap.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_52",
      "id": "fn-52"
    }
  }, [_vm._v("52")])]), _vm._v(" The L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" prescribes six different offerings, ordains Aaron and his sons as priests, and allows the priests to bring him offerings for the priesthood and the people. Moses and Aaron then can enter the tent of meeting, and the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" demonstrates that he accepts the offerings. This resolves the first conflict.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("But the conflict flares up again in Leviticus 10: “Now Aaron’s sons, Nadab and Abihu, each took his censer, put fire in it, and laid incense on it; and they offered "), _c('em', [_vm._v("unholy fire")]), _vm._v(" before the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(", such as he had not commanded them."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_53",
      "id": "fn-53"
    }
  }, [_vm._v("53")])]), _vm._v(" And fire came out from the presence of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" and consumed them, and they died before the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v("” (vv 1–2). Nadab and Abihu defiled the tent of meeting by offering "), _c('em', [_vm._v("unholy fire")]), _vm._v(" before the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" and then polluted the tent with their corpses."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_54",
      "id": "fn-54"
    }
  }, [_vm._v("54")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" responded by telling Aaron, “You are to distinguish between "), _c('em', [_vm._v("the holy and the common")]), _vm._v(", and between "), _c('em', [_vm._v("the unclean and the clean")]), _vm._v("” (Lev 10:10). In reverse order, the following chapters in Leviticus teach just that. The L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" gives detailed laws on how to distinguish between clean and unclean (Lev 11–15), reaching a climax on the Day of Atonement when the high priest may enter the holy of holies, the most holy place, “to make atonement for the people of Israel once in the year for all their sins” (Lev 16:34)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_55",
      "id": "fn-55"
    }
  }, [_vm._v("55")])]), _vm._v(" Chapters 17–22 continue with the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v("’"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("S")]), _vm._v(" instructions concerning the holy and the common, reaching a climax with the institution of the Sabbath and other festivals (Lev 23–25). In the outcome of the narrative the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" promises blessings for obedience and punishments for disobedience (Lev 26). Thus we can picture Leviticus as a complex plot:")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/21.jpg"
    }
  })]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-7"
    }
  }, [_vm._v("Historical Interpretation")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("In historical interpretation we ask, Who wrote this text, to whom, when, where, and why? These questions focus on the identity of the author, the original addressees, the approximate period of writing, the social and geographical setting, and the goal or purpose for writing. Leviticus says some thirty times (see n. 62 below) that the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" instructed Moses what to say to God’s covenant people Israel. Whether Moses himself wrote the text or one or more scribes did and whether additions were later incorporated remain open questions."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_56",
      "id": "fn-56"
    }
  }, [_vm._v("56")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("When and where did the events recorded in Leviticus take place? Brevard Childs observes that “the book of Leviticus has been given a definite historical setting as instructions to Moses in the context of the Sinai covenant. Even elements of the narrative are continued from the previous book (Lev 8–9 join Exod 29). The final chapters look forward to the imminent entrance into the promised land and connect smoothly with the book of Numbers.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_57",
      "id": "fn-57"
    }
  }, [_vm._v("57")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Childs points out the importance of the link of Leviticus with the Sinai covenant: “The canonical effect of structuring the book of Leviticus in such a way as to connect all the material directly to the revelation at Sinai is of crucial importance in understanding its role as authoritative scripture for Israel. The laws of Leviticus which stemmed originally from very different periods, and which reflected remarkably different sociological contexts, are subordinated to the one overarching theological construct, namely, the divine will made known to Moses at Sinai for every succeeding generation.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_58",
      "id": "fn-58"
    }
  }, [_vm._v("58")])]), _vm._v(" For preaching purposes, therefore, we need not get sidetracked into the maze of different theories regarding the original historical settings of individual laws; we can concentrate on the meaning of these laws in their canonical context and culture."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_59",
      "id": "fn-59"
    }
  }, [_vm._v("59")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("As to the question of why Leviticus was written, Raymond Dillard and Tremper Longman suggest, “The purpose of the book is to provide guidelines to priests and laypeople concerning appropriate behavior in the presence of a holy God, thus the emphasis is on communicating information, not on subtle or artificial literary plays.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_60",
      "id": "fn-60"
    }
  }, [_vm._v("60")])]), _vm._v(" Baruch Levine broadens the book’s purpose by linking it to God’s purpose for Israel: “Leviticus takes its cue from the covenant charge delivered in Exodus 19:5–6: ‘Indeed, all the earth is Mine, but you shall be My kingdom of priests and a holy nation.’ How Israel was to realize the divine program is the burden of the Book of Leviticus.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_61",
      "id": "fn-61"
    }
  }, [_vm._v("61")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-8"
    }
  }, [_vm._v("Theocentric Interpretation")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("In theocentric interpretation we ask, Where is the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" God in this text? It is clear that Leviticus centers on the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(", for the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" is the "), _c('em', [_vm._v("subject")]), _vm._v(", the source, of all the laws and most of the messages. The introductory phrase “The L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" said to [spoke to] Moses” is repeated some thirty times."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_62",
      "id": "fn-62"
    }
  }, [_vm._v("62")])]), _vm._v(" “Seventeen of the twenty-seven chapters begin with the formula ‘And the Lord said….’ Leviticus, more than any other OT book, claims to be a divine word for humanity.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_63",
      "id": "fn-63"
    }
  }, [_vm._v("63")])]), _vm._v(" Moreover, in some forty of the laws obedience is motivated by the phrase “I am the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(".”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_64",
      "id": "fn-64"
    }
  }, [_vm._v("64")])]), _vm._v(" Furthermore, “the covenant relationship and the law as an expression of that relationship are presented in the Old Testament as the revealed will of God.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_65",
      "id": "fn-65"
    }
  }, [_vm._v("65")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" is not only the subject of the messages but also the "), _c('em', [_vm._v("object")]), _vm._v(" of Israel’s worship at the tabernacle. When the tabernacle was completed, “the cloud covered the tent of meeting, and the glory of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" filled the tabernacle” (Exod 40:34). The L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(", who had revealed himself to Moses as the great I AM, was literally present in the midst of his people. Gordon Wenham observes, “God is always present with Israel in a real way. On occasion his presence becomes both visible and tangible. This idea is expressed times without number in Leviticus. The enduring presence of God is one of the theological presuppositions running through the whole book. God is preeminently present in worship.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_66",
      "id": "fn-66"
    }
  }, [_vm._v("66")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Wenham adds, “God is present not only in worship, but at all times, even in the mundane duties of life. Leviticus knows of nothing that is beyond God’s control or concern. The whole of man’s life must be lived out in the presence of God. The recurring refrain in the later chapters, ‘I am the Lord your God’ (e.g., 18:2ff.; 19:3–4, 10; 20:7), reminds the people of Israel that every aspect of their life—religion (chs. 21–24), sex (chs. 18 and 20), relations with neighbors (chs. 19, 25)—is of concern to their covenant redeemer.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_67",
      "id": "fn-67"
    }
  }, [_vm._v("67")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-9"
    }
  }, [_vm._v("Christocentric Interpretation")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Finally, Leviticus must be understood in its broadest canonical context—that is, the New Testament. Here the question is, how does the original message God gave to Old Testament Israel connect with his revelation in the New Testament, specifically with his Word made flesh, Jesus Christ? In my book "), _c('em', [_vm._v("Preaching Christ from the Old Testament")]), _vm._v(" I defined “preaching Christ” as “preaching sermons which authentically integrate the message of the text with the climax of God’s revelation in the person, work, and/or teaching of Jesus Christ as revealed in the New Testament.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_68",
      "id": "fn-68"
    }
  }, [_vm._v("68")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("I further identified seven (sometimes overlapping) ways that preachers can use to move legitimately from an Old Testament passage to Jesus Christ in the New Testament. These seven ways are redemptive-historical progression, promise-fulfillment, typology, analogy, longitudinal themes, New Testament references, and contrast. I will briefly explain each of these."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_69",
      "id": "fn-69"
    }
  }, [_vm._v("69")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('em', [_vm._v("Redemptive-historical progression")]), _vm._v(" considers how the message of the passage changes as redemptive history progresses from the text’s historical setting in the Old Testament to Jesus’ first and/or second coming.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('em', [_vm._v("Promise-fulfillment")]), _vm._v(" moves from God’s promise in the Old Testament of a coming Messiah to his fulfillment of that promise in Jesus’ first and/or second coming. It is not likely that we will use the way of promise-fulfillment much for Leviticus since the genre of law, unlike that of prophecy, does not contain many promises of the coming Messiah.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('em', [_vm._v("Typology")]), _vm._v(", by contrast, can be used frequently in preaching Leviticus because the priests and sacrifices that its laws regulate are types of Christ. Typology looks for both analogy and escalation as it moves from an Old Testament type prefiguring Jesus to the antitype, Jesus himself."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_70",
      "id": "fn-70"
    }
  }, [_vm._v("70")])]), _vm._v(" Dillard and Longman point out, “The book of Hebrews provides guidance in that it presents Jesus Christ as the perfect High Priest who offers himself as the perfect sacrifice. As it says in Hebrews 9:26: ‘But now he has appeared once for all at the end of the ages to do away with sin by the sacrifice of himself.’ … The Aaronic priesthood and the Old Testament sacrificial system all anticipated a greater reality, namely Jesus Christ as final priest and sufficient sacrifice (Heb 4:14–5:10; 7–10; see also Rom 8:3; Eph 5:2).”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_71",
      "id": "fn-71"
    }
  }, [_vm._v("71")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('em', [_vm._v("Analogy")]), _vm._v(" notes the similarity between the teaching (theme) or goal of the text and the teaching or goal of a New Testament passage speaking of Jesus. Analogies exist because through Christ Christians are grafted into Israel (e.g., Rom 11:17–24; Eph 2:11–22). Since the church is the new Israel, one can find analogies between what God promised Israel and what Christ promises the church and between what God required of Israel and what God, through Christ, requires of the church.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('em', [_vm._v("Longitudinal themes")]), _vm._v(" is a technical term in biblical theology for uncovering themes that run through several Bible books and even through both the Old and the New Testaments."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_72",
      "id": "fn-72"
    }
  }, [_vm._v("72")])]), _vm._v(" The way of longitudinal themes traces the theme (or subtheme) of the text through the Old Testament to Jesus Christ in the New Testament.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('em', [_vm._v("New Testament references")]), _vm._v(" usually support the other six ways to Christ by quoting New Testament verses that cite or allude to the preaching text and link it to Christ.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('em', [_vm._v("Contrast")]), _vm._v(" moves to Christ by noting the contrast between the message of the text and that of the New Testament—a contrast that exists because Christ has come or because Christ teaches the opposite.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-3"
    }
  }, [_vm._v("Ten Steps in Preaching Christ from Leviticus"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_73",
      "id": "fn-73"
    }
  }, [_vm._v("73")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-10"
    }
  }, [_vm._v("1. Select the preaching text.")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The preaching text must be a literary unit and contain a vital theme. Leviticus frequently signals a new unit or subunit with the phrase “The L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" said [spoke] to Moses.”")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-11"
    }
  }, [_vm._v("2. Read the text in its literary context.")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Read and reread the text in its context and jot down initial questions. The literary context of the preaching text is first of all “the collection or series in which an individual law appears,”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_74",
      "id": "fn-74"
    }
  }, [_vm._v("74")])]), _vm._v(" next the book of Leviticus, next the other law genres in the Pentateuch, especially Deuteronomy. This is where the “analogous interpretation” (mentioned at n. 45 above in the quotation by Julie Smith) comes in."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_75",
      "id": "fn-75"
    }
  }, [_vm._v("75")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-12"
    }
  }, [_vm._v("3. Outline the structure of the text.")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("In the Hebrew note the major affirmations and other literary structures. Mark major units with headings and verse references.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-13"
    }
  }, [_vm._v("4. Interpret the text in its own historical setting.")]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("a. Literary interpretation.")]), _vm._v(" As explained above, in literary interpretation we pay particular attention to the meaning of the words by investigating literary features such as the text’s genre, grammar, syntax, figures of speech, scenes, character description, plotline, and rhetorical structures such as repetition, inclusio, and chiasm. We should also “note the style of the law. Is it a categorical assertion (apodictic law) or is it for a specific situation (case law)?”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_76",
      "id": "fn-76"
    }
  }, [_vm._v("76")])]), _vm._v(" We also look for the format of the law: Is it absolute or conditional? And we should note any so-called motive clauses, which explain why one should obey the law.")]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("b. Historical interpretation")]), _vm._v(". As noted above, in historical interpretation we ask, Who wrote this text, to whom, when, where, and why? The “why” question focuses on the original purpose of the laws, their intent or goal."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_77",
      "id": "fn-77"
    }
  }, [_vm._v("77")])]), _vm._v(" We “endeavor to understand the original meaning of laws in light of their cultural background.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_78",
      "id": "fn-78"
    }
  }, [_vm._v("78")])]), _vm._v(" We ask, “What was the original intent, significance, and purpose of the law? What was its point … ? What was the law designed to accomplish? What problem was it attempting to correct? What behavior or attitude was it attempting to encourage? Why was it issued? Here ancient Near Eastern comparative material can often be helpful.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_79",
      "id": "fn-79"
    }
  }, [_vm._v("79")])])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("c. Theocentric interpretation")]), _vm._v(". As explained above, in theocentric interpretation we ask, Where is the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" God in this text? We noted that God is the "), _c('em', [_vm._v("subject")]), _vm._v(" of the laws: it is God’s law; these are the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v("’"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("S")]), _vm._v(" covenant stipulations. Moreover, in some forty of the laws obedience is motivated by the phrase “I am the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(".” The L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" is also the "), _c('em', [_vm._v("object")]), _vm._v(" of many laws: Israel is to keep God’s laws because he has redeemed them. Israel is to obey these laws to the glory of God. Israel is to imitate God: “Be holy, for I am holy” (Lev 11:44–45 [2×]; 19:2; 20:7, 26)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_80",
      "id": "fn-80"
    }
  }, [_vm._v("80")])]), _vm._v(" “The laws on sacrifice say repeatedly that the ceremonies take place ‘before the Lord’; the food offerings make ‘a soothing aroma for the Lord’ (e.g., 1:9, 13, 17; 2:9; 3:5).”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_81",
      "id": "fn-81"
    }
  }, [_vm._v("81")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Review the results of these four steps with the help of some good commentaries.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-14"
    }
  }, [_vm._v("5. Formulate the text’s theme, goal, and need.")]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("a. Textual theme")]), _vm._v(". State the textual theme in a brief sentence that summarizes the "), _c('em', [_vm._v("message")]), _vm._v(" of the text for its original hearers: subject and predicate. What is the text "), _c('em', [_vm._v("saying")]), _vm._v("?")]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("b")]), _vm._v(". "), _c('em', [_vm._v("The author’s goal")]), _vm._v(". State the goal (purpose) of the author for his original hearers. What is the text "), _c('em', [_vm._v("doing")]), _vm._v("? Does the author aim to persuade, to motivate, to urge, to warn, to comfort? In the law genre the author usually seeks to "), _c('em', [_vm._v("urge")]), _vm._v(" obedience or to "), _c('em', [_vm._v("motivate")]), _vm._v(" obedience with motive clauses. The goal in Leviticus is usually the third use of the law, "), _c('em', [_vm._v("usus normativus")]), _vm._v("—that is, to urge Israel to obey the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v("’"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("S")]), _vm._v(" covenant stipulations.")]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("c")]), _vm._v(". "), _c('em', [_vm._v("The need addressed.")]), _vm._v(" State the need the author addressed—that is, the question behind the text. Christopher Wright suggests some questions to ask: “What kind of bad situation was this law trying to prevent? What kind of better situation was this law trying to promote? Whom was this law aiming to protect? Who would have benefitted from this law and why? … Whose needs was this law trying to provide for, and how did it do so?”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_82",
      "id": "fn-82"
    }
  }, [_vm._v("82")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-15"
    }
  }, [_vm._v("6. Understand the message in the contexts of canon and redemptive history.")]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("a. Canonical interpretation.")]), _vm._v(" Understand the message in the context of the whole Bible.")]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("b. Redemptive-historical interpretation.")]), _vm._v(" Understand the message in the context of redemptive history from creation to new creation.")]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("c. Christocentric interpretation.")]), _vm._v(" Check which of the seven ways of preaching Christ lead most directly to Jesus Christ in the New Testament.")]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("(1) Redemptive-historical progression")]), _vm._v(". God’s covenant with Israel and its stipulations progress to Christ’s new covenant with his church and its stipulations. In response to a lawyer’s question, “Teacher, which commandment in the law is the greatest?” Jesus replied, “‘You shall love the Lord your God with all your heart, and with all your soul, and with all your mind.’ This is the greatest and first commandment. And a second is like it: ‘You shall love your neighbor as yourself.’ On these two commandments hang all the law and the prophets” (Matt 22:36–40). The central love commandment is like a solid nail on which the whole Old Testament hangs. “Jesus’ words imply that every law in the writings of Moses—and elsewhere in the Hebrew Scriptures—must be understood as a command to express some facet of love for God and humanity. All Mosaic law was fundamentally a call to live a life of love.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_83",
      "id": "fn-83"
    }
  }, [_vm._v("83")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In his Sermon on the Mount, Jesus set forth the deeper meaning of Old Testament law. For example, he said, “You have heard that it was said to those of ancient times, ‘You shall not murder’; and ‘whoever murders shall be liable to judgment.’ But I say to you that if you are angry with a brother or sister, you will be liable to judgment; and if you insult a brother or sister, you will be liable to the council; and if you say, ‘You fool,’ you will be liable to the hell of fire” (Matt 5:21–22). Again: “You have heard that it was said, ‘You shall not commit adultery.’ But I say to you that everyone who looks at a woman with lust has already committed adultery with her in his heart” (Matt 5:27–28). Again: “You have heard that it was said, ‘An eye for an eye and a tooth for a tooth.’ But I say to you, Do not resist an evildoer. But if anyone strikes you on the right cheek, turn the other also; and if anyone wants to sue you and take your coat, give your cloak as well; and if anyone forces you to go one mile, go also the second mile. Give to everyone who begs from you, and do not refuse anyone who wants to borrow from you” (Matt 5:38–42; see also vv 43–48).")]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("(2) Promise-fulfillment")]), _vm._v(". In contrast to prophecy, which contains many promises of the coming Messiah, one would not expect to find many such promises in the genre of law. Yet, because Israel could not keep God’s law perfectly, God’s law does contain indirect promises by looking forward to the One who can keep God’s law perfectly and who can pay for our sins."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_84",
      "id": "fn-84"
    }
  }, [_vm._v("84")])])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("(3) Typology")]), _vm._v(". We may not use the way of promise-fulfilment much in preaching Leviticus, but we’ll use the way of typology all the more, for the ceremonial laws in Leviticus prefigured the life and ministry of Christ. Robert Bergen argues that “the New Testament leaves no doubt that aspects of Israelite worship were divinely intended to be representative of realities found uniquely in the person of Christ.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_85",
      "id": "fn-85"
    }
  }, [_vm._v("85")])]), _vm._v(" In fact, Gordon Wenham observes, “It is in terms of these sacrifices that Jesus himself and the early church understood his atoning death.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_86",
      "id": "fn-86"
    }
  }, [_vm._v("86")])]), _vm._v(" As the ceremonial laws provided “a means of access to God via sacrifice and holiness rules,” so the New Testament teaches that today we have direct access to God through Jesus Christ."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_87",
      "id": "fn-87"
    }
  }, [_vm._v("87")])])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("(4) Analogy")]), _vm._v(". The way of analogy looks for analogies between the old covenant and the new, between old covenant stipulations and new covenant stipulations. As God made covenant with Israel, so God in Christ made covenant with us. As God made certain demands of Israel, so God through Christ makes certain demands of us. We can note the similarity between God’s demands of Old Testament Israel and Christ’s demands of his New Testament church.")]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("(5) Longitudinal themes")]), _vm._v(". The way of longitudinal themes traces the theme or subtheme of the text through the Old Testament to Christ in the New Testament. For example, specific laws calling for justice, love of God, love of neighbor, and rest can be traced through the Old Testament to Christ in the New Testament. Preachers should handle longitudinal themes with care lest all the details lead to information overload and a boring sermon.")]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("(6) New Testament references")]), _vm._v(". New Testament references can be used independently as a single way to preaching Christ, but it is more likely that they will be used as a way to support one or more of the other six ways of preaching Christ. One can find appropriate New Testament references by checking such sources as cross-reference Bibles, concordances, the appendix of Nestle-Aland’s "), _c('em', [_vm._v("Novum Testamentum Graece")]), _vm._v(","), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_88",
      "id": "fn-88"
    }
  }, [_vm._v("88")])]), _vm._v(" and "), _c('em', [_vm._v("The Treasury of Scripture Knowledge")]), _vm._v(", consisting of 500,000 Scripture references and parallel passages."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_89",
      "id": "fn-89"
    }
  }, [_vm._v("89")])])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("(7) Contrast")]), _vm._v(". A final legitimate road to Christ in the New Testament is the way of contrast. I place it last on the list because it is better to travel the positive roads to Christ than this negative one. But this way is legitimate because it recognizes that there is discontinuity in the midst of continuity. Many Old Testament laws no longer hold for Christians. We do not worship at a tabernacle, do not offer animal sacrifices, do not need a priest as intermediary to approach God—all because Christ has come. Christ is our eternal Priest who offered himself as a once-for-all sacrifice (cf. Heb 9:1–10:18).")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-16"
    }
  }, [_vm._v("7. Formulate the sermon theme, goal, and need addressed.")]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("a. Sermon theme")]), _vm._v(". Ideally, your sermon theme will be the same as your textual theme (Step 5a). If Step 6 forces a change, stay as close as possible to the textual theme lest your text not support your sermon theme. Your theme will guide especially the development of the body of the sermon.")]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("b. Sermon goal")]), _vm._v(". Your goal with the sermon must be in harmony with the author’s goal (Step 5b) and match the sermon theme. Your goal will guide the style of the sermon as well as the content of its conclusion (application). Christopher Wright suggests some questions preachers should raise for contemporary application:")]), _vm._v(" "), _c('ul', [_c('li', [_vm._v("What kinds of situations in our society are similar to those in the Old Testament laws, and in what ways?")]), _vm._v(" "), _c('li', [_vm._v("What kind of people in our society are facing needs that are like the needy in Israel, and why? …")]), _vm._v(" "), _c('li', [_vm._v("What kind of protection and provision do some people need? …")]), _vm._v(" "), _c('li', [_vm._v("How could the principles found in Old Testament laws be applied in practical life today—in my own life, in the church, in wider society? …")]), _vm._v(" "), _c('li', [_vm._v("How, then, can I preach and teach this part of Scripture in a way that is faithful to its original context and purpose but also relevant to my own people now? How can I make this text “speak again,” so that people today see its relevance and are moved to obey God in response?"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_90",
      "id": "fn-90"
    }
  }, [_vm._v("90")])])])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_c('em', [_vm._v("c. Need addressed.")]), _vm._v(" State the need you are addressing. This need should be similar to the need addressed by the author. The need will inform the content of your sermon introduction.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-17"
    }
  }, [_vm._v("8. Select a suitable sermon form.")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Select a sermon form that respects the form of the text (didactic or narrative, deductive or inductive) and that achieves the goal of the sermon. Historical narrative in Leviticus (Lev 8–10; 24:10–23) should be preached in narrative form. Because of the “storyline time movement” in Leviticus,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_91",
      "id": "fn-91"
    }
  }, [_vm._v("91")])]), _vm._v(" other laws can also be preached in a narrative form (see, e.g., ch. 7 below on Lev 16), though usually the didactic form is most appropriate.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-18"
    }
  }, [_vm._v("9. Prepare the sermon outline.")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("If possible, follow the flow of the text (Step 3) in the body of the sermon. Main points, derived from the text, support the theme. The introduction should expose the need addressed. The conclusion should clinch your goal.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-19"
    }
  }, [_vm._v("10. Write the sermon in oral style.")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Say it out loud as you write it. Write in oral style, using short sentences, vivid words, strong nouns and verbs, active voice, present tense, images, and illustrations.")]), _vm._v(" "), _c('p', {
    staticClass: "image-f"
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/foot.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-1",
      "id": "fn_1"
    }
  }, [_vm._v("1.")]), _vm._v(" Donald E. Gowan, "), _c('em', [_vm._v("Reclaiming the Old Testament for the Christian Pulpit")]), _vm._v(" (Edinburgh: T&T Clark, 1981), 79.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-2",
      "id": "fn_2"
    }
  }, [_vm._v("2.")]), _vm._v(" David A. Dorsey, “The Use of OT Law in the Christian Life: A Theocentric Approach,” "), _c('em', [_vm._v("Evangelical Journal")]), _vm._v(" 17, no. 1 (1999): 1. Mark W. Elliott, in his "), _c('em', [_vm._v("Engaging Leviticus: Reading Leviticus Theologically with Its Past Interpreters")]), _vm._v(" (Eugene, OR: Cascade Books, 2012), xxiii–xliii, presents an interesting “Gallery of Major Interpreters of Leviticus” from Clement of Alexandria (d. 211) to the present, followed by 312 pages of commentary quoting many of these interpreters on each chapter in Leviticus. Without denying the value of past Jewish and Christian interpretations, in the interest of brevity I will, with few exceptions, only quote and interact with contemporary commentators.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-3",
      "id": "fn_3"
    }
  }, [_vm._v("3.")]), _vm._v(" Roy Gane, "), _c('em', [_vm._v("Leviticus, Numbers")]), _vm._v(", NIVAC (Grand Rapids: Zondervan, 2004), 23.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-4",
      "id": "fn_4"
    }
  }, [_vm._v("4.")]), _vm._v(" Arie C. Leder, "), _c('em', [_vm._v("Waiting for the Land: The Story Line of the Pentateuch")]), _vm._v(" (Phillipsburg, NJ: P&R, 2010), 137–38.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-5",
      "id": "fn_5"
    }
  }, [_vm._v("5.")]), _vm._v(" Jacob Milgrom, "), _c('em', [_vm._v("Leviticus: A Book of Ritual and Ethics")]), _vm._v(", CC (Minneapolis: Fortress, 2004), 1.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-6",
      "id": "fn_6"
    }
  }, [_vm._v("6.")]), _vm._v(" John E. Hartley, "), _c('em', [_vm._v("Leviticus")]), _vm._v(", WBC 4 (Dallas: Word, 1992), ix.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-7",
      "id": "fn_7"
    }
  }, [_vm._v("7.")]), _vm._v(" Tremper Longman III, "), _c('em', [_vm._v("Making Sense of the Old Testament: Three Crucial Questions")]), _vm._v(" (Grand Rapids: Baker, 1998), 105. See pp. 105–8. On p. 105 Longman quotes C. I. Scofield, "), _c('em', [_vm._v("Rightly Dividing the Word of Truth")]), _vm._v(", 5: “Scripture never, in "), _c('em', [_vm._v("any")]), _vm._v(" dispensation, mingles these two principles [of law and grace].” See also Dorsey, “Use of OT Law,” 2–5.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-8",
      "id": "fn_8"
    }
  }, [_vm._v("8.")]), _vm._v(" Longman, "), _c('em', [_vm._v("Making Sense of the Old Testament")]), _vm._v(", 105. For an evaluation of theonomy, see also Vern Sheridan Poythress, "), _c('em', [_vm._v("The Shadow of Christ in the Law of Moses")]), _vm._v(" (Brentwood, TN: Wolgemuth & Hyatt, 1991), 331–61, and Mark F. Rooker, "), _c('em', [_vm._v("Leviticus")]), _vm._v(", NAC 3A (Nashville: Broadman & Holman, 2000), 69–75.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-9",
      "id": "fn_9"
    }
  }, [_vm._v("9.")]), _vm._v(" For five different views, see Wayne G. Strickland, ed., "), _c('em', [_vm._v("The Law, the Gospel, and the Modern Christian: Five Views")]), _vm._v(" (Grand Rapids: Zondervan, 1993). The five views presented are “A Reformed Perspective,” “The Theonomic Reformed Approach,” “The Law as God’s Gracious Guidance for the Promotion of Holiness,” “A Dispensational View,” and “A Modified Lutheran View,” each with responses from the other contributors.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-10",
      "id": "fn_10"
    }
  }, [_vm._v("10.")]), _vm._v(" Dorsey, “Use of OT Law,” 5–10.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-11",
      "id": "fn_11"
    }
  }, [_vm._v("11.")]), _vm._v(" Luther, “Preface to the Old Testament,” par. 4, "), _c('em', [_vm._v("Luther’s Works")]), _vm._v(" (St. Louis: Concordia, 2009) 35, 236–37—American Edition. For more details on Luther’s hermeneutics, see my "), _c('em', [_vm._v("Preaching Christ from the Old Testament: A Contemporary Hermeneutical Method")]), _vm._v(" (Grand Rapids: Eerdmans, 1999), 111–26.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-12",
      "id": "fn_12"
    }
  }, [_vm._v("12.")]), _vm._v(" William D. Thompson, "), _c('em', [_vm._v("Preaching Biblically")]), _vm._v(" (Nashville: Abingdon, 1981), 111.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-13",
      "id": "fn_13"
    }
  }, [_vm._v("13.")]), _vm._v(" John Calvin, "), _c('em', [_vm._v("Institutes of the Christian Religion")]), _vm._v(", ed. John T. McNeill, trans. Ford Lewis Battles (Philadelphia: Westminster, 1960), 2.7.12. Cf. "), _c('em', [_vm._v("Institutes")]), _vm._v(" 2.7.6–17. For more details on Calvin’s hermeneutics, see my "), _c('em', [_vm._v("Preaching Christ from the Old Testament")]), _vm._v(", 127–51.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-14",
      "id": "fn_14"
    }
  }, [_vm._v("14.")]), _vm._v(" E.g., Duane Garrett, “Preaching the Law,” "), _c('em', [_vm._v("Faith and Mission")]), _vm._v(" 13, no. 1 (Fall 1995): 13, writes, “The Law itself recognizes no such categories. For the ancient Israelite, there was only the one Law. All was from God and all demanded obedience.” See also, e.g., Richard E. Averbeck, “Law,” in "), _c('em', [_vm._v("Cracking Old Testament Codes")]), _vm._v(", ed. D. Brent Sandy and Donald L. Giese Jr. (Nashville: Broadman & Holman, 1995), 123–24; Gowan, "), _c('em', [_vm._v("Reclaiming the Old Testament")]), _vm._v(", 79; Gordon J. Wenham, "), _c('em', [_vm._v("The Book of Leviticus")]), _vm._v(" (Grand Rapids: Eerdmans, 1979), 32; and Dorsey, “Use of the OT Law,” 10 n. 14, listing several commentaries.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-15",
      "id": "fn_15"
    }
  }, [_vm._v("15.")]), _vm._v(" “Israel’s community knew no such thing as ‘secular,’ because their covenant relationship to God came to bear on every aspect of their existence.” Ken Mathews, “Preaching in the Pentateuch,” in "), _c('em', [_vm._v("Handbook of Contemporary Preaching")]), _vm._v(", ed. Michael Duduit (Nashville: Broadman, 1992), 272. Cf. p. 276: “The law was a whole, not susceptible to the dissection of moral versus ceremonial.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-16",
      "id": "fn_16"
    }
  }, [_vm._v("16.")]), _vm._v(" Calvin, "), _c('em', [_vm._v("Institutes")]), _vm._v(" 4.20.14. Cf. Longman, "), _c('em', [_vm._v("Making Sense of the Old Testament")]), _vm._v(", 116: “A closer look at the case law reveals that these specific laws are not laws in addition to the Ten Commandments. On the contrary, the specific laws flow from the general ethical principles asserted in the commandments.” Longman refers to Walter C. Kaiser Jr.’s analysis of Deuteronomy in "), _c('em', [_vm._v("Toward Old Testament Ethics")]), _vm._v(" (Grand Rapids: Zondervan, 1983), 127–37.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-17",
      "id": "fn_17"
    }
  }, [_vm._v("17.")]), _vm._v(" “The word "), _c('em', [_vm._v("torah")]), _vm._v(" probably comes from a Hebrew verb, meaning ‘to point [out the direction that one should go in].’ … God’s law was meant to be a light on one’s path; it was to point a person in the direction he or she should go.” Walter C. Kaiser Jr., "), _c('em', [_vm._v("Preaching and Teaching from the Old Testament: A Guide for the Church")]), _vm._v(" (Grand Rapids: Baker, 2003), 140.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-18",
      "id": "fn_18"
    }
  }, [_vm._v("18.")]), _vm._v(" Gordon J. Wenham, “Grace and Law in the Old Testament,” in "), _c('em', [_vm._v("Law, Morality and the Bible: A Symposium")]), _vm._v(", ed. Bruce Kay and Gordon Wenham (Downers Grove, IL: InterVarsity Press, 1978), 3–23, here 9.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-19",
      "id": "fn_19"
    }
  }, [_vm._v("19.")]), _vm._v(" Wenham, “Grace and Law,” 10 and 17. See also Wenham, "), _c('em', [_vm._v("Book of Leviticus")]), _vm._v(", 29–32, and Hartley, "), _c('em', [_vm._v("Leviticus,")]), _vm._v(" lxiv–lxvii. Cf. Averbeck, “Law,” 115: “The Mosaic law is imbedded within the Mosaic covenant (see Exod 19:5; 24:7–8, Lev 26:9; 42–45, Deut 5:2–3; 29:1, 9), and the ritual legislation in Exodus 25 through Leviticus 16 is an integral part of the Mosaic law.” Cf. Averbeck, “Law,” 116–17: “To capture the spirit of this legal institution is to realize that a covenant is best defined as a means of expressing and/or a method of establishing and defining a relationship…. Understanding this is important lest the Mosaic covenant—within which the Mosaic law is imbedded—be considered primarily a legal contract rather than a relational bond. Covenants were not the same as legal contracts: A contract was a business transaction; a covenant was about two parties relating to one another on a long-term basis.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-20",
      "id": "fn_20"
    }
  }, [_vm._v("20.")]), _vm._v(" William W. Klein, Craig L. Blomberg, and Robert L. Hubbard Jr., "), _c('em', [_vm._v("Introduction to Biblical Interpretation")]), _vm._v(", rev. ed. (Nashville: Thomas Nelson, 2004), 345.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-21",
      "id": "fn_21"
    }
  }, [_vm._v("21.")]), _vm._v(" Cf. Pss 1; 119; 147:19–20.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-22",
      "id": "fn_22"
    }
  }, [_vm._v("22.")]), _vm._v(" “We can learn from the use of the word ‘fulfil’ elsewhere in Matthew’s Gospel, in connection with prophecy, that it means ‘give the full meaning to.’” Robin Nixon, “Fulfilling the Law: The Gospels and Acts,” in "), _c('em', [_vm._v("Law, Morality and the Bible")]), _vm._v(", ed. Bruce Kaye and Gordon Wenham (Downers Grove, IL: InterVarsity Press, 1978), 53–71, here 56.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-23",
      "id": "fn_23"
    }
  }, [_vm._v("23.")]), _vm._v(" Jesus’ opposition to the scribes and Pharisees and Paul’s negative statements about the law should be understood as a polemic against the rabbinic misunderstanding of keeping the law in order to be saved. Cf. Rom 7:4; Gal 2:15–16; 3:25. See Elizabeth Achtemeier, "), _c('em', [_vm._v("Preaching from the Old Testament")]), _vm._v(" (Louisville: Westminster John Knox, 1989), 103–5, and Rooker, "), _c('em', [_vm._v("Leviticus")]), _vm._v(", 67–69. See also Kaiser, "), _c('em', [_vm._v("Toward Old Testament Ethics")]), _vm._v(", 308–14. On pp. 309–10 Kaiser suggests four lines of thought for resolving the tension between some of Paul’s statements:")]), _vm._v(" "), _c('p', {
    staticClass: "foot-h1"
  }, [_vm._v("“(1) For Paul, the law is explicitly “the law of God” (Rom 7:22, 25; 8:7; cf. 1 Cor 7:19). Therefore it continues to deserve our greatest respect, for it comes with divine authority.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-h"
  }, [_vm._v("(2) Often when Paul appears to be disparaging the law, he is instead debunking “legalism.” …")]), _vm._v(" "), _c('p', {
    staticClass: "foot-h"
  }, [_vm._v("(3) Some Pauline phrases are abbreviated, as the context will usually show, and therefore wrong inferences may be made if one is not careful….")]), _vm._v(" "), _c('p', {
    staticClass: "foot-h"
  }, [_vm._v("(4) Paul does regard the law as abolished when he means the ceremonial ordinances contained in the law.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-24",
      "id": "fn_24"
    }
  }, [_vm._v("24.")]), _vm._v(" “The law written on the heart is for Jeremiah the guarantee that the old external law will be kept.” Wenham, “Grace and Law,” 16.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-25",
      "id": "fn_25"
    }
  }, [_vm._v("25.")]), _vm._v(" Wenham, “Grace and Law,” 18.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-26",
      "id": "fn_26"
    }
  }, [_vm._v("26.")]), _vm._v(" “The rabbis understood the Noachian precepts, which they took to preclude idolatry, murder, and adultery and the eating of flesh with the blood, to be binding upon Gentiles.” Robin Nixon, “The Universality of the Concept of Law,” in B. Kaye and G. Wenham (eds.), "), _c('em', [_vm._v("Law, Morality and the Bible")]), _vm._v(" (Downers Grove, IL: InterVarsity Press, 1978), 115.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-27",
      "id": "fn_27"
    }
  }, [_vm._v("27.")]), _vm._v(" Jay Sklar, "), _c('em', [_vm._v("Leviticus: An Introduction and Commentary")]), _vm._v(", TOTC 3 (Downers Grove, IL: InterVarsity Press, 2014), 57, 58. See also pp. 61–62: “In sum, though not every law in Leviticus is in force today, and though the expression of the laws that are in force may differ, the values embodied in all of these laws are just as applicable today as then. These values are in fact to be embodied by the people of God as a way of showing to the world the holy and gracious character of the Lord, the one who gives these laws so that his people might prosper in their ways and create a society of goodness, justice, mercy, holiness and love.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-28",
      "id": "fn_28"
    }
  }, [_vm._v("28.")]), _vm._v(" Averbeck, “Law,” 124–25.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-29",
      "id": "fn_29"
    }
  }, [_vm._v("29.")]), _vm._v(" Wenham, "), _c('em', [_vm._v("Book of Leviticus")]), _vm._v(", 35.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-30",
      "id": "fn_30"
    }
  }, [_vm._v("30.")]), _vm._v(" Longman, "), _c('em', [_vm._v("Making Sense of the Old Testament")]), _vm._v(", 121.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-31",
      "id": "fn_31"
    }
  }, [_vm._v("31.")]), _vm._v(" J. Daniel Hays, “Applying the OT Law Today,” "), _c('em', [_vm._v("Bibliotheca Sacra")]), _vm._v(" 158 (2001): 21–35, here 32, provides the following characteristics of a principle: “(a) It should be reflected in the text, (b) it should be timeless, (c) it should correspond to the theology of the rest of Scripture, (d) it should not be culturally bound, and (e) it should be relevant to both Old Testament and current New Testament believers. These universal principles will often be related directly to the character of God and His holiness, the nature of sin, the issue of obedience, or concern for other people.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-32",
      "id": "fn_32"
    }
  }, [_vm._v("32.")]), _vm._v(" Jacob Milgrom, "), _c('em', [_vm._v("Leviticus 1–16: A New Translation with Introduction and Commentary")]), _vm._v(", AB 3 (New York: Doubleday, 1991), 1, with references to m. Meg. 3:5 and m. Menah. 4:3.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-33",
      "id": "fn_33"
    }
  }, [_vm._v("33.")]), _vm._v(" Baruch A. Levine, "), _c('em', [_vm._v("Leviticus = Va-yikra: The Traditional Hebrew Text with the New JPS Translation")]), _vm._v(", JPS Torah Commentary (Philadelphia: Jewish Publication Society, 1989), xi.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-34",
      "id": "fn_34"
    }
  }, [_vm._v("34.")]), _vm._v(" “Basically, chaps. 1–16 are addressed to the priests, while chaps. 17–27 focus on priestly instructions for the people.” Walter C. Kaiser Jr., “The Book of Leviticus,” in "), _c('em', [_vm._v("The New Interpreter’s Bible: Old Testament Survey")]), _vm._v(" (Nashville: Abingdon, 2005), 34.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-35",
      "id": "fn_35"
    }
  }, [_vm._v("35.")]), _vm._v(" Hays, “Applying the OT Law Today,” 26.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-36",
      "id": "fn_36"
    }
  }, [_vm._v("36.")]), _vm._v(" Sklar, "), _c('em', [_vm._v("Leviticus")]), _vm._v(", 28. For details see pp. 28–29.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-37",
      "id": "fn_37"
    }
  }, [_vm._v("37.")]), _vm._v(" Sklar, "), _c('em', [_vm._v("Leviticus")]), _vm._v(", 30. See also Leder, "), _c('em', [_vm._v("Waiting for the Land")]), _vm._v(", 23–41.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-38",
      "id": "fn_38"
    }
  }, [_vm._v("38.")]), _vm._v(" Leder, "), _c('em', [_vm._v("Waiting for the Land")]), _vm._v(", 34–35.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-39",
      "id": "fn_39"
    }
  }, [_vm._v("39.")]), _vm._v(" L. Michael Morales, "), _c('em', [_vm._v("Who Shall Ascend the Mountain of the Lord? A Biblical Theology of the Book of Leviticus")]), _vm._v(" (Downers Grove, IL: InterVarsity Press, 2015), 51.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-40",
      "id": "fn_40"
    }
  }, [_vm._v("40.")]), _vm._v(" “This descent of Yahweh God into his dwelling place amidst Israel marks a cataclysmic, historic event: for the first time since before the flood, the maker of heaven and earth has a dwelling among humanity.” Morales, "), _c('em', [_vm._v("Who Shall Ascend")]), _vm._v(", 113.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-41",
      "id": "fn_41"
    }
  }, [_vm._v("41.")]), _vm._v(" Morales, "), _c('em', [_vm._v("Who Shall Ascend")]), _vm._v(", 23.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-42",
      "id": "fn_42"
    }
  }, [_vm._v("42.")]), _vm._v(" Kenneth Mathews, "), _c('em', [_vm._v("Leviticus: Holy God, Holy People")]), _vm._v(" (Wheaton, IL: Crossway, 2009), 13.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-43",
      "id": "fn_43"
    }
  }, [_vm._v("43.")]), _vm._v(" R. A. Barclay, "), _c('em', [_vm._v("The Law Givers: Leviticus and Deuteronomy")]), _vm._v(" (Nashville: Abingdon, 1964), 18.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-44",
      "id": "fn_44"
    }
  }, [_vm._v("44.")]), _vm._v(" Wenham, "), _c('em', [_vm._v("Book of Leviticus")]), _vm._v(", 18 n. 25. Cf. Hartley, "), _c('em', [_vm._v("Leviticus")]), _vm._v(", lvi–lxiii.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-45",
      "id": "fn_45"
    }
  }, [_vm._v("45.")]), _vm._v(" Julie M. Smith, “Point Our Souls to Christ: Lessons from Leviticus,” "), _c('em', [_vm._v("Studies in the Bible and Antiquity")]), _vm._v(" 1 (2009): 67–82, here 67, with credit for this insight to Mary Douglas, "), _c('em', [_vm._v("Leviticus as Literature")]), _vm._v(" (Oxford: Oxford University Press, 2000), 18.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-46",
      "id": "fn_46"
    }
  }, [_vm._v("46.")]), _vm._v(" "), _c('em', [_vm._v("The NIV Study Bible: New International Version")]), _vm._v(" (Grand Rapids: Zondervan, 1985), 146.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-47",
      "id": "fn_47"
    }
  }, [_vm._v("47.")]), _vm._v(" For many reasons for its centrality, see Christophe Nihan, "), _c('em', [_vm._v("From Priestly Torah to Pentateuch: A Study in the Composition of the Book of Leviticus")]), _vm._v(" (Tübingen: Mohr Siebeck, 2007), 95–105.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-48",
      "id": "fn_48"
    }
  }, [_vm._v("48.")]), _vm._v(" W. Shea, “Literary Form and Theological Function in Leviticus,” in "), _c('em', [_vm._v("The Seventy Weeks, Leviticus and the Nature of Prophecy")]), _vm._v(", ed. F. Holbrook, Daniel and Revelation Committee 3 (Washington, DC: Biblical Research Institute, 1986), 131–68, esp. 149, as found in Gane, "), _c('em', [_vm._v("Leviticus, Numbers")]), _vm._v(", 31. Cf. Erich Zenger’s outline in "), _c('em', [_vm._v("Einleitung in das Alte Testament")]), _vm._v(" (Stuttgart: Kohlhammer, 1995), 37, as found in Leder, "), _c('em', [_vm._v("Waiting for the Land")]), _vm._v(", 136:")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/18.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-49",
      "id": "fn_49"
    }
  }, [_vm._v("49.")]), _vm._v(" Hays, “Applying OT Law Today,” 25, with examples of “time sequence phrases” in n. 10: “Then Moses took” (Lev 8:10), “He then presented” (8:14), “Moses then said” (8:31), “On the eighth day Moses summoned” (9:1), “So Aaron came to the altar” (9:8), “So fire came out from the presence of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" and consumed them” (10:2), “The L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" spoke to Moses after the death of the two sons of Aaron” (16:1). See also Leder, "), _c('em', [_vm._v("Waiting for the Land")]), _vm._v(", 116–19.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-50",
      "id": "fn_50"
    }
  }, [_vm._v("50.")]), _vm._v(" Nihan, "), _c('em', [_vm._v("From Priestly Torah to Pentateuch")]), _vm._v(", 88–89.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-51",
      "id": "fn_51"
    }
  }, [_vm._v("51.")]), _vm._v(" See the usual plotlines in my "), _c('em', [_vm._v("The Modern Preacher and the Ancient Text: Interpreting and Preaching Biblical Literature")]), _vm._v(" (Grand Rapids: Eerdmans, 1988), 203–5, and "), _c('em', [_vm._v("Preaching Christ from Genesis: Foundations for Expository Sermons")]), _vm._v(" (Grand Rapids: Eerdmans, 2007), 4, 21, 46, 65, 88, 105, etc.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-52",
      "id": "fn_52"
    }
  }, [_vm._v("52.")]), _vm._v(" Nihan, "), _c('em', [_vm._v("From Priestly Torah to Pentateuch")]), _vm._v(", 90–91.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-53",
      "id": "fn_53"
    }
  }, [_vm._v("53.")]), _vm._v(" Nihan, "), _c('em', [_vm._v("From Priestly Torah to Pentateuch")]), _vm._v(", 92: “The narrator’s comment … that the offerings of Aaron’s sons had "), _c('em', [_vm._v("not")]), _vm._v(" been commanded by Yahweh … deliberately breaks with the pattern observable in Lev 8–9, where each main ritual sequence is concluded by the phrase … [‘as the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" commanded’] (see Lev 8:4, 9, 13, 17, 21, 29, 36; 9:7, 10, 21).”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-54",
      "id": "fn_54"
    }
  }, [_vm._v("54.")]), _vm._v(" Since the corpses of Nadab and Abihu “were unclean and transmitted impurity by contact with them, they threatened to defile the priests and desecrate the sanctuary. They were therefore removed by their tunics, without being touched, and buried in the cemetery outside the camp.” John W. Kleinig, "), _c('em', [_vm._v("Leviticus")]), _vm._v(", ConcC (Saint Louis: Concordia, 2003), 233.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-55",
      "id": "fn_55"
    }
  }, [_vm._v("55.")]), _vm._v(" “Leviticus 16, which not only caps the second section (chs. 11–15) but also serves as the pivot for the book’s two halves, begins the concern with [sacred, cultic] time.” Morales, "), _c('em', [_vm._v("Who Shall Ascend")]), _vm._v(", 201.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-56",
      "id": "fn_56"
    }
  }, [_vm._v("56.")]), _vm._v(" For succinct discussions of various theories regarding authorship and date, see Wenham, "), _c('em', [_vm._v("Book of Leviticus")]), _vm._v(", 8–13; Hartley, "), _c('em', [_vm._v("Leviticus")]), _vm._v(", xxxv–xliii; Richard S. Hess, “Leviticus,” in "), _c('em', [_vm._v("The Expositor’s Bible Commentary")]), _vm._v(", ed. Tremper Longman III and David A. Garland, rev. ed. (Grand Rapids: Zondervan, 2008), 565–72; and Sklar, "), _c('em', [_vm._v("Leviticus")]), _vm._v(", 30–35.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-57",
      "id": "fn_57"
    }
  }, [_vm._v("57.")]), _vm._v(" Brevard S. Childs, "), _c('em', [_vm._v("Old Testament Theology in a Canonical Context")]), _vm._v(" (Philadelphia: Fortress, 1986), 157. On p. 158 Childs adds, “The material in Leviticus has not been merely organized in a roughly topical fashion, but it has been characteristically rendered by the repetition of key phrases”—for example,")]), _vm._v(" "), _c('p', {
    staticClass: "foot-ext"
  }, [_vm._v("Lev 1–3: ‘a pleasing odour to Yahweh,’ 1:9, 13, 17; 2:2, 9, 12; 3:5, 16 (cf. 4:31; 23:13; 26:31)")]), _vm._v(" "), _c('p', {
    staticClass: "foot-ext1"
  }, [_vm._v("4–5: ‘the priest shall make atonement … they shall be forgiven,’ 4:20, 26, 31, 36; 5:6, 10, 16, 18")]), _vm._v(" "), _c('p', {
    staticClass: "foot-ext1"
  }, [_vm._v("6–7: ‘this is the law of …,’ 6:9, 14, 25; 7:1, 11, 37….")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-58",
      "id": "fn_58"
    }
  }, [_vm._v("58.")]), _vm._v(" Childs, "), _c('em', [_vm._v("Old Testament Theology")]), _vm._v(", 54. Cf. Bruce C. Birch, "), _c('em', [_vm._v("Let Justice Roll Down: The Old Testament, Ethics, and Christian Life")]), _vm._v(" (Louisville: Westminster John Knox, 1991), 158: “Israelite law is handed on to us in a narrative setting…. All the legal material in Exodus and Leviticus appears in the context of Israel’s experience in the encampment and covenant making at Sinai. This is interpretively important in spite of the fact that some of these materials, the Leviticus legal material in particular, originated historically at a much later time. It has become important that these laws be read through the norm of the Sinai covenant…. The narrative context makes clear that for Israel all law receives its authority from the belief that it has been divinely revealed as a part of the solemn covenant established by God with Israel and agreed to by Israel at Sinai.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-59",
      "id": "fn_59"
    }
  }, [_vm._v("59.")]), _vm._v(" Cf. Rolf Rendtorff, “Is It Possible to Read Leviticus as a Separate Book?,” in "), _c('em', [_vm._v("Reading Leviticus: A Conversation with Mary Douglas")]), _vm._v(", ed. John F. A. Sawyer, JSOTSup 227 (Sheffield: Sheffield Academic, 1996), 22–35, here 34: “The exegetical task is not to reconstruct earlier levels that always will remain hypothetical and dependent on the respective methodological approach of the interpreter. Rather exegetes have to try to understand the biblical text in its given form and shape.” Cf. Rooker, "), _c('em', [_vm._v("Leviticus")]), _vm._v(", 23–39, for an extended critique of the Documentary Hypothesis.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-60",
      "id": "fn_60"
    }
  }, [_vm._v("60.")]), _vm._v(" Raymond B. Dillard and Tremper Longman III, "), _c('em', [_vm._v("An Introduction to the Old Testament")]), _vm._v(" (Grand Rapids: Zondervan, 1994), 76. Cf. Hartley, "), _c('em', [_vm._v("Leviticus")]), _vm._v(", xxxii: “The purpose of Leviticus … is to preserve divine sermons for the instruction of the congregation in cultic and ethical matters.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-61",
      "id": "fn_61"
    }
  }, [_vm._v("61.")]), _vm._v(" Levine, "), _c('em', [_vm._v("Leviticus")]), _vm._v(", xi, with references to Jer 18:18, Hag 2:10–13, and Mal 2:6–7. Cf. Wenham, "), _c('em', [_vm._v("Book of Leviticus")]), _vm._v(", 50: “The laws of Leviticus … were designed to mold Israel into a holy people in a particular historical environment.” Cf. Kaiser, “Book of Leviticus,” 34: “The book is given to Israel so that the people might live holy lives in fellowship with a holy God…. A greater purpose is also served in furnishing Israel with laws that secure their well-being: They are to be a blessing to the nations…. Consequently, one of the key purposes for the law of Leviticus is to prepare Israel for its world mission.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-62",
      "id": "fn_62"
    }
  }, [_vm._v("62.")]), _vm._v(" Lev 1:1; 4:1; 5:14; 6:1, 8, 19, 24; 7:22, 28; 8:1; 11:1; 12:1; 13:1; 14:1, 33; 15:1; 16:1; 19:1; 20:1; 21:1; 22:17, 26; 23:1, 9, 23, 26, 33; 25:1; 27:1.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-63",
      "id": "fn_63"
    }
  }, [_vm._v("63.")]), _vm._v(" Kaiser, “Book of Leviticus,” 34.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-64",
      "id": "fn_64"
    }
  }, [_vm._v("64.")]), _vm._v(" Lev 11:44, 45; 18:2, 4, 5, 21, 30; 19:3, 4, 10, 12, 14, 16, 18, 25, 30, 31, 32, 34, 36; 20:7, 8, 24, 26; 21:8, 15, 23; 22:2, 3, 8, 9, 16, 30, 31; 24:22; 25:17, 55; 26:1, 2, 44, 45.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-65",
      "id": "fn_65"
    }
  }, [_vm._v("65.")]), _vm._v(" Birch, "), _c('em', [_vm._v("Let Justice Roll Down")]), _vm._v(", 166.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-66",
      "id": "fn_66"
    }
  }, [_vm._v("66.")]), _vm._v(" Wenham, "), _c('em', [_vm._v("Book of Leviticus")]), _vm._v(", 16. Cf. Hartley, "), _c('em', [_vm._v("Leviticus")]), _vm._v(", lxiii–lxiv.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-67",
      "id": "fn_67"
    }
  }, [_vm._v("67.")]), _vm._v(" Wenham, "), _c('em', [_vm._v("Book of Leviticus")]), _vm._v(", 17.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-68",
      "id": "fn_68"
    }
  }, [_vm._v("68.")]), _vm._v(" Greidanus, "), _c('em', [_vm._v("Preaching Christ from the Old Testament")]), _vm._v(", 10.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-69",
      "id": "fn_69"
    }
  }, [_vm._v("69.")]), _vm._v(" For an extended explanation, see my "), _c('em', [_vm._v("Preaching Christ from the Old Testament")]), _vm._v(", 203–77.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-70",
      "id": "fn_70"
    }
  }, [_vm._v("70.")]), _vm._v(" See my "), _c('em', [_vm._v("Preaching Christ from the Old Testament")]), _vm._v(", 257–61.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-71",
      "id": "fn_71"
    }
  }, [_vm._v("71.")]), _vm._v(" Dillard and Longman, "), _c('em', [_vm._v("Introduction to the Old Testament")]), _vm._v(", 82.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-72",
      "id": "fn_72"
    }
  }, [_vm._v("72.")]), _vm._v(" See my "), _c('em', [_vm._v("Modern Preacher")]), _vm._v(", 67–70, and "), _c('em', [_vm._v("Preaching Christ from the Old Testament")]), _vm._v(", 222–24.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-73",
      "id": "fn_73"
    }
  }, [_vm._v("73.")]), _vm._v(" I developed these ten steps for all genres of biblical literature for first-year preaching students. I include them here in expanded form to address some unique issues that need to be raised for the genre of law.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-74",
      "id": "fn_74"
    }
  }, [_vm._v("74.")]), _vm._v(" Klein, Blomberg, and Hubbard, "), _c('em', [_vm._v("Introduction to Biblical Interpretation")]), _vm._v(", 349, urge students to “investigate surrounding laws for interpretive clues.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-75",
      "id": "fn_75"
    }
  }, [_vm._v("75.")]), _vm._v(" Cf. Averbeck, “Law,” 131: “Compare laws in the biblical text with one another.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-76",
      "id": "fn_76"
    }
  }, [_vm._v("76.")]), _vm._v(" Averbeck, “Law,” 130. Cf. p. 120: “The analysis of OT law has focused for several decades on the distinction between laws that unconditionally and categorically assert right and wrong (apodictic law) and laws that define specific cases and prescribe legal consequences (case or casuistic law).” For more details on this and other distinctions, see Klein, Blomberg, and Hubbard, "), _c('em', [_vm._v("Introduction to Biblical Interpretation")]), _vm._v(", 341–44.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-77",
      "id": "fn_77"
    }
  }, [_vm._v("77.")]), _vm._v(" In order to avoid needless duplication, in the following chapters we shall cover historical interpretation under Step 5, “Formulate the text’s theme, "), _c('em', [_vm._v("goal")]), _vm._v(", and need.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-78",
      "id": "fn_78"
    }
  }, [_vm._v("78.")]), _vm._v(" Klein, Blomberg, and Hubbard, "), _c('em', [_vm._v("Introduction to Biblical Interpretation")]), _vm._v(", 349.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-79",
      "id": "fn_79"
    }
  }, [_vm._v("79.")]), _vm._v(" Dorsey, “Use of OT Law,” 13. Cf. Averbeck, “Law,” 131: “Look for parallels with the ancient Near Eastern literature, after paying close attention to inner-biblical parallels. The analogues in the biblical world are valuable especially when the laws assume that the reader is aware of this extra-biblical material. This is especially important for ritual legislation.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-80",
      "id": "fn_80"
    }
  }, [_vm._v("80.")]), _vm._v(" Dorsey, “Use of OT Law,” 14, expands on this command by raising the questions, “What does the law reveal about God and his ways? What does it reveal about God’s mind, personality, qualities, attitudes, priorities, assumptions, presuppositions, values, concerns, teaching methodologies, and the kinds of attitudes and moral and ethical standards he wants to see in those who serve him?”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-81",
      "id": "fn_81"
    }
  }, [_vm._v("81.")]), _vm._v(" Wenham, "), _c('em', [_vm._v("Book of Leviticus")]), _vm._v(", 16.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-82",
      "id": "fn_82"
    }
  }, [_vm._v("82.")]), _vm._v(" Christopher J. H. Wright, "), _c('em', [_vm._v("How to Preach and Teach the Old Testament for All Its Worth")]), _vm._v(" (Grand Rapids: Zondervan, 2016), 177.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-83",
      "id": "fn_83"
    }
  }, [_vm._v("83.")]), _vm._v(" Robert D. Bergen, “Preaching Old Testament Law,” in "), _c('em', [_vm._v("Reclaiming the Prophetic Mantle: Preaching the Old Testament Faithfully")]), _vm._v(", ed. George L. Klein (Nashville: Broadman, 1992), 51–69, here 57. Cf. Rom 13:8–10, “Owe no one anything, except to love one another; for the one who loves another has fulfilled the law. The commandments, ‘You shall not commit adultery; You shall not murder; You shall not steal; You shall not covet’; and any other commandment, are summed up in this word, ‘Love your neighbor as yourself.’ Love does no wrong to a neighbor; therefore, love is the fulfilling of the law.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-84",
      "id": "fn_84"
    }
  }, [_vm._v("84.")]), _vm._v(" Cf. Gal 3:23–24, “Now before faith came, we were imprisoned and guarded under the law until faith would be revealed. Therefore the law was our disciplinarian until Christ came, so that we might be justified by faith.” Cf. Rom 3:21–22, “But now, apart from law, the righteousness of God has been disclosed, and is attested by the law and the prophets, the righteousness of God through faith in Jesus Christ for all who believe.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-85",
      "id": "fn_85"
    }
  }, [_vm._v("85.")]), _vm._v(" Bergen, “Preaching Old Testament Law,” 63.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-86",
      "id": "fn_86"
    }
  }, [_vm._v("86.")]), _vm._v(" Wenham, "), _c('em', [_vm._v("Book of Leviticus")]), _vm._v(", 37.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-87",
      "id": "fn_87"
    }
  }, [_vm._v("87.")]), _vm._v(" Gowan, "), _c('em', [_vm._v("Reclaiming the Old Testament")]), _vm._v(", 94. Gowan continues, “The meaning and value of the old ritual is not denied, but it becomes a type to help us understand the work of Christ on our behalf (see especially the Letter to the Hebrews).”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-88",
      "id": "fn_88"
    }
  }, [_vm._v("88.")]), _vm._v(" I am using the 28th Revised Edition of 2012.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-89",
      "id": "fn_89"
    }
  }, [_vm._v("89.")]), _vm._v(" "), _c('em', [_vm._v("The Treasury of Scripture Knowledge")]), _vm._v(". New York: Revell, 1889. Available on Logos software.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-90",
      "id": "fn_90"
    }
  }, [_vm._v("90.")]), _vm._v(" This bulleted list is taken directly from Wright, "), _c('em', [_vm._v("How to Preach")]), _vm._v(", 178–79"), _c('em', [_vm._v(".")]), _vm._v(" Cf. Bergen, “Preaching Old Testament Law,” 62–63.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-91",
      "id": "fn_91"
    }
  }, [_vm._v("91.")]), _vm._v(" See n. 49 above.")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-6b35aa7c","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/010_Chapter01.vue
// module id = 84
// module chunks = 0